import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import DiscussMenu from '../components/DiscussMenu';
import ViewSwitcher from '../components/ViewSwitcher';
import { decodeContext, registerViewState } from '../utils/collab';
import { getCsrfToken } from '../utils/auth';
import { ExportModal } from '../components/ExportModal';
import { AddItemModal } from '../components/AddItemModal';
import FunnelView from '../components/FunnelView';
import GraphView from '../components/GraphView';
import TraceDashboard from '../components/TraceDashboard';
import { formatStampShort } from '../utils/datetime';
import { useFilterCache } from '../components/FilterSelect';
import { filterListingUrl } from '../utils/orcanosLinks';
import '../styles/MatrixPage.css';

// Turn a failed fetch into a message a human can read. `data` is the parsed JSON body
// (null when the response wasn't JSON — e.g. an IIS/proxy HTML error page). Covers the
// common proxy-level statuses that never reach the app so the graph doesn't just say
// "Failed (404)".
function readError(data, resp, action = 'complete the request') {
  if (data && (data.detail || data.error)) return data.detail || data.error;
  const status = resp ? resp.status : 0;
  const byStatus = {
    401: 'Your session has expired — please sign in again.',
    403: 'You do not have permission to do this.',
    404: 'The server could not find this request (it may be too large for the server to accept).',
    413: 'The request was too large for the server to accept.',
    414: 'The request was too large for the server to accept.',
    429: 'Too many requests — please wait a moment and try again.',
    502: 'The server is temporarily unavailable (bad gateway).',
    503: 'The server is temporarily unavailable.',
    504: 'The server took too long to respond (timeout).',
  };
  if (byStatus[status]) return byStatus[status];
  if (status >= 500) return `Server error (${status}) while trying to ${action}.`;
  return `Could not ${action} (${status || 'network error'}).`;
}

// Graph view shows a fixed, legible slice of L1 chains per page (a full 50 would
// be a hairball), paged independently of the matrix via its own cache namespace.
// The toolbar's view switcher (ViewSwitcher, "lens" design, 2026-10-27).
const VIEW_MODES = [
  { id: 'dashboard', label: 'Dashboard', icon: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="M2 14h12" /><rect x="3" y="8" width="2.4" height="5" /><rect x="6.8" y="4" width="2.4" height="9" /><rect x="10.6" y="6" width="2.4" height="7" /></svg> },
  { id: 'matrix', label: 'Matrix', icon: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><rect x="1.5" y="1.5" width="13" height="13" rx="1.5" /><path d="M1.5 6h13M1.5 10.5h13M6 1.5v13" /></svg> },
  { id: 'funnel', label: 'Funnel', icon: <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M1 2h14l-5 6v5l-4 2V8z" /></svg> },
  { id: 'graph', label: 'Graph', icon: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><circle cx="3" cy="8" r="2" /><circle cx="13" cy="3.5" r="2" /><circle cx="13" cy="12.5" r="2" /><path d="M4.8 7l6.4-3M4.8 9l6.4 3" /></svg> },
];

const GRAPH_PAGE_SIZE_OPTIONS = [10, 25, 50];
const GRAPH_PAGE_SIZE_KEY = 'graphPageSize';

// True when this page load was a real browser reload (F5), not SPA navigation.
// On F5 we hard-refresh the active view from Orcanos instead of serving cache.
const IS_RELOAD = (() => {
  try {
    const nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    if (nav) return nav.type === 'reload';
    return performance.navigation && performance.navigation.type === 1; // legacy
  } catch {
    return false;
  }
})();
// Consumed once, by whichever view is active on the reload, so a later SPA
// navigation doesn't keep forcing hard refreshes.
let reloadPending = IS_RELOAD;
const takeReload = () => {
  if (reloadPending) { reloadPending = false; return true; }
  return false;
};

// Small plus glyph for the inline "add child item" affordance
const PlusIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M6 1.5v9M1.5 6h9" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
  </svg>
);

// Small "info" glyph — the on-hover trigger for the item details card
const InfoIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="6.6" stroke="currentColor" strokeWidth="1.4" />
    <path d="M8 7.1v3.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <circle cx="8" cy="5.1" r="0.95" fill="currentColor" />
  </svg>
);

// Expand all / Collapse all: chevrons apart (unfold) vs. chevrons together (fold)
const UnfoldMoreIcon = () => (
  <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path d="M4 5 7 2l3 3M4 9l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const UnfoldLessIcon = () => (
  <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path d="M4 2.5 7 5.5l3-3M4 11.5l3-3 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Small, crisp expand/collapse chevron (replaces the oversized ▼ text glyph)
const ChevronIcon = () => (
  <svg viewBox="0 0 10 10" fill="none" aria-hidden="true">
    <path d="M2 3.2 5 6.2 8 3.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Map an arbitrary status string to a pill colour class
const statusPillClass = (value) => {
  const v = String(value).toLowerCase();
  if (/(approv|done|closed|complete|pass|resolved)/.test(v)) return 'st-good';
  if (/(reopen|progress|pending|fail|block|hold|reject)/.test(v)) return 'st-warn';
  if (/(new|open|draft|todo|backlog)/.test(v)) return 'st-info';
  return 'st-neutral';
};

// Map a test-run result to its colour class. Order matters: "Fail (Stop)" must
// be tested before plain "Fail". Mirrors the funnel's colour language, with
// extra granularity for the stop/blocker variants.
const testRunPillClass = (value) => {
  const v = String(value).toLowerCase();
  if (/fail\s*\(?\s*stop/.test(v)) return 'tr-failstop'; // stronger red
  if (/block/.test(v)) return 'tr-blocker';              // grey
  if (/fail/.test(v)) return 'tr-fail';                  // red
  if (/pass/.test(v)) return 'tr-pass';                  // green
  if (/(no\s*run|not\s*in\s*execution|not\s*executed)/.test(v)) return 'tr-norun'; // yellow
  return 'tr-neutral';
};

// A test-run status/result column, matched by name (mirrors RUN_RESULT_COL in Dashboard.jsx).
const RUN_RESULT_COL = /run.*(status|result)/i;

// Render the inner content of a cell: blank for empty (no "-" placeholder),
// pills for Status, tinted text for Key, HTML passthrough where the value
// contains markup, plain text otherwise.
const renderCellBody = (col, cellContent, url = '') => {
  const isEmpty = !cellContent.value || cellContent.value === '-';
  if (isEmpty) return null;
  if (cellContent.isHtml) {
    // .cell-html lets the no-wrap mode flatten the markup's block elements to one line
    return <span className="cell-html" dangerouslySetInnerHTML={{ __html: cellContent.value }} />;
  }
  if (col === 'Status') {
    return <span className={`status-pill ${statusPillClass(cellContent.value)}`}>{cellContent.value}</span>;
  }
  // Any run status/result column, whatever the tenant named it ("Last Test Run Result",
  // "Last Test Run status", …) — same rule as the dashboard card. "…Run Version" never matches.
  if (RUN_RESULT_COL.test(col)) {
    return <span className={`status-pill ${testRunPillClass(cellContent.value)}`}>{cellContent.value}</span>;
  }
  if (col === 'Key') {
    // Link the Key to its Orcanos item page; stopPropagation so clicking the
    // link doesn't also toggle the L1 row's collapse.
    if (url) {
      return (
        <a
          className="cell-key cell-key-link"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          {cellContent.value}
        </a>
      );
    }
    return <span className="cell-key">{cellContent.value}</span>;
  }
  return cellContent.value;
};

// Plain-text form of a cell value, for the truncated cell's hover tooltip: strips
// markup and collapses whitespace so a clamped Description is still readable.
const cellTooltip = (cellContent) => {
  if (!cellContent || !cellContent.value) return undefined;
  const text = cellContent.isHtml
    ? String(cellContent.value).replace(/<[^>]*>/g, ' ')
    : String(cellContent.value);
  const clean = text.replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim();
  return clean.length > 60 ? clean : undefined;
};

// Deepest present level in a full-path row → the next level is a genuine
// missing trace (mirrors the dashboard's "Missing" logic). Uses the ORIGINAL
// row, so deduplicated blank cells are never mistaken for gaps. Returns that
// level index, or -1 when the chain reaches the last configured level.
const missingColumnForRow = (row, levels) => {
  // Prefer the backend's block-aware annotation: it knows whether a short branch is a
  // genuine gap (its block has deeper levels configured) or a legitimate branch end.
  if (row && 'missing_at_depth' in row) {
    if (row.missing_at_depth == null) return -1;
    return levels.findIndex(l => l.level_num === row.missing_at_depth);
  }
  // Legacy fallback for older cached results without the annotation.
  let deepest = -1;
  for (let i = 0; i < levels.length; i++) {
    if (row[`l${levels[i].level_num}`]) deepest = i;
  }
  return deepest >= 0 && deepest < levels.length - 1 ? deepest + 1 : -1;
};

// "Missing" pill shown where traceability ends before the last level
const MissingIndicator = () => (
  <span className="missing-indicator" title="No traceability at this level">
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
    </svg>
    Missing
  </span>
);

// Depth at which a Test Case traces to a Test Run block but has no run yet → the run
// simply wasn't executed. This is NOT a gap (backend keeps missing_at_depth null), so it
// gets its own neutral light-blue pill instead of the amber "Missing" warning.
const notExecutedColumnForRow = (row, levels) => {
  if (row && row.not_executed_at_depth != null) {
    return levels.findIndex(l => l.level_num === row.not_executed_at_depth);
  }
  return -1;
};

// One status for a requirement across ALL its rows (the status dot + the filter bar).
// A gap on any branch wins, then a test that never ran; otherwise it is traced to the end.
// Built on the same per-row rules as the pills, so the dot can never disagree with them.
const groupStatus = (group, levels) => {
  let notExec = false;
  for (const row of group.children) {
    if (missingColumnForRow(row, levels) !== -1) return 'gap';
    if (notExecutedColumnForRow(row, levels) !== -1) notExec = true;
  }
  return notExec ? 'notexec' : 'ok';
};

const GROUP_STATUS_LABEL = { gap: 'Has a gap', notexec: 'Test not executed', ok: 'Traced to the end' };

// The filter bar's status pills ("Not executed" only shows when this page has one).
const STATUS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'gap', label: 'With gaps' },
  { id: 'notexec', label: 'Not executed' },
  { id: 'ok', label: 'Fully traced' },
];

// localStorage read that never throws (private window, blocked storage).
const readPref = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    return v !== null ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
};

// "Not Executed" pill — a covered test whose run hasn't happened yet
const NotExecutedIndicator = () => (
  <span className="notexec-indicator" title="Test exists but has not been executed">
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v4a1 1 0 00.293.707l2.5 2.5a1 1 0 001.414-1.414L11 9.586V7z" clipRule="evenodd" />
    </svg>
    Not Executed
  </span>
);

// Floating item information card shown on hover (details from QW_Get_Object).
// Anchored to the info icon; the pointer can move onto it (to click "Open in Orcanos").
const ItemInfoCard = ({ card, onEnter, onLeave }) => {
  if (!card) return null;
  const { x, y, url, loading, error, data } = card;
  const W = 340;
  const left = Math.min(x + 8, (typeof window !== 'undefined' ? window.innerWidth : 1200) - W - 12);
  const top = Math.min(y + 6, (typeof window !== 'undefined' ? window.innerHeight : 800) - 60);
  return (
    <div
      className="item-card"
      style={{ left, top, width: W }}
      role="tooltip"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      {loading && <div className="item-card-loading"><span className="item-card-spinner" /> Loading…</div>}
      {!loading && error && <div className="item-card-error">Couldn’t load item: {error}</div>}
      {!loading && !error && data && (
        <>
          <div className="item-card-head">
            {data.key && <span className="item-card-key">{data.key}</span>}
            {data.status && (
              <span className={`status-pill ${statusPillClass(data.status)}`}>{data.status}</span>
            )}
          </div>
          <div className="item-card-title">{data.title}</div>
          {data.description && <div className="item-card-desc">{data.description}</div>}
          {Array.isArray(data.fields) && data.fields.length > 0 && (
            <dl className="item-card-fields">
              {data.fields.slice(0, 8).map((f, i) => (
                <div className="item-card-field" key={i}>
                  <dt>{f.label}</dt>
                  <dd>{f.value}</dd>
                </div>
              ))}
            </dl>
          )}
          {url && (
            <div className="item-card-actions">
              <a className="item-card-open" href={url} target="_blank" rel="noopener noreferrer">
                Open in Orcanos
                <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path d="M5.5 2.5H2.5v9h9v-3M8 2.5h3.5V6M11 3 6 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Floating menu opened by a single ＋ when the next depth mixes several block types.
// Lists one "Add <type>" row per type; a backdrop closes it on outside-click.
const AddMenu = ({ menu, onClose }) => {
  if (!menu) return null;
  const { x, y, options, onPick } = menu;
  const W = 240;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const left = Math.min(x, vw - W - 12);
  const top = Math.min(y + 6, vh - (options.length * 40 + 20));
  return (
    <>
      <div className="add-menu-backdrop" onClick={onClose} />
      <div className="add-menu" style={{ left, top, width: W }} role="menu">
        {options.map((o, i) => (
          <button
            key={i}
            type="button"
            className="add-menu-item"
            role="menuitem"
            onClick={() => onPick(o.blk)}
          >
            <PlusIcon />
            <span>Add {o.label}</span>
          </button>
        ))}
      </div>
    </>
  );
};

// Floating card shown when hovering the ⓘ in a level's grid header: where this level's
// items come from (project + version, work-item type, filter id). A depth can hold
// several blocks (branching tree), so each block gets its own section.
// The card stays open while the mouse is on it (onEnter/onLeave), so the ↗ filter link can be clicked.
const LevelInfoCard = ({ card, onEnter, onLeave }) => {
  if (!card) return null;
  const { x, y, name, blocks } = card;
  const W = 300;
  const left = Math.max(12, Math.min(x - W, (typeof window !== 'undefined' ? window.innerWidth : 1200) - W - 12));
  const top = Math.min(y, (typeof window !== 'undefined' ? window.innerHeight : 800) - 80);
  return (
    <div className="level-info-card" style={{ left, top, width: W }} role="tooltip"
         onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <div className="level-info-card-head">{name}</div>
      {blocks.map((b, i) => (
        <dl className="level-info-card-fields" key={i}>
          {blocks.length > 1 && <div className="level-info-card-sep" />}
          <div className="level-info-card-field">
            <dt>Project</dt>
            <dd>{b.project || '—'}</dd>
          </div>
          <div className="level-info-card-field">
            <dt>Work item</dt>
            <dd>{b.type || '—'}</dd>
          </div>
          <div className="level-info-card-field">
            <dt>Filter</dt>
            <dd className="level-info-card-filter">
              {b.filter ? (
                <>
                  <span className="level-info-card-filter-text" title={b.filterName ? `${b.filter} ${b.filterName}` : b.filter}>
                    {b.filter}{b.filterName ? ` ${b.filterName}` : ''}
                  </span>
                  {b.filterUrl && (
                    <a className="level-info-card-open" href={b.filterUrl} target="_blank" rel="noopener noreferrer"
                       title="Open this filter in Orcanos (new tab)" aria-label="Open this filter in Orcanos (new tab)">↗</a>
                  )}
                </>
              ) : '—'}
            </dd>
          </div>
          {b.orphanFilter && (
            <div className="level-info-card-field">
              <dt>Orphans filter</dt>
              <dd>{b.orphanFilter}</dd>
            </div>
          )}
        </dl>
      ))}
    </div>
  );
};

// A stored UTC stamp ("2026-07-09 14:22:31"), shown as UTC and labelled — see utils/datetime.
const formatBuiltAt = (s) => formatStampShort(s);

// The columns that name an item; a block may hide some, never all (export.py IDENTITY_COLUMNS).
const IDENTITY_COLUMNS = ['Key', 'Name', 'Synopsis'];

/** An item's human key, however this row was built. `columns.Key` is what the grid
 *  prints; `key`/`name` are the shapes older rows carry. */
function itemKeyOf(item) {
  if (!item) return '';
  return String((item.columns && item.columns.Key) || item.key || item.name || '').trim();
}

/** The DEEPEST item on a row — the one a "discuss this" almost always means. A gap row's
 *  deepest item is the last one that exists, which is exactly the thing to talk about. */
function deepestOnRow(row, levels) {
  for (let i = levels.length - 1; i >= 0; i -= 1) {
    const item = row[`l${levels[i].level_num}`];
    if (item) return { item, level: levels[i].level_num };
  }
  return null;
}

function MatrixPage() {
  const { setupId } = useParams();
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  // A row somebody pointed at from a chat card, and the right-click "Discuss with…" menu.
  const [focusKey, setFocusKey] = useState('');
  const [discussAt, setDiscussAt] = useState(null);
  const [panelName, setPanelName] = useState('');    // shown in the toolbar so the panel is identifiable in full view
  const [configuredLevels, setConfiguredLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [matrixCachedAt, setMatrixCachedAt] = useState(null); // set when the page came from cache
  const [searchInput, setSearchInput] = useState('');  // what's typed in the L1 search box
  const [search, setSearch] = useState('');            // the applied L1 search term (drives fetch)
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    try {
      const stored = localStorage.getItem('matrixRowsPerPage');
      return stored ? parseInt(stored, 10) : 50;
    } catch {
      return 50;
    }
  });
  // Inline add-item + trace
  const [addCtx, setAddCtx] = useState(null);        // { levelNum, parentKey, childType, childKeyPrefix, l1Id }
  const [typeLabels, setTypeLabels] = useState({});  // work-item code -> full label (e.g. REQ -> "Product Requirement")
  // project_id|version_id -> { name, version } and project_id -> name, so the level
  // header's ⓘ card can show "Cardiology Monitor (2.0)" instead of raw ids.
  const [projectLookup, setProjectLookup] = useState({ byCombo: {}, byProject: {} });
  // Add permission is PER work-item type, PER project (QW_Login Item_type[].Permission) — NOT a
  // global flag. Shape: { [projectId]: { [typeCode]: canAdd(bool) } }. Gates each ＋ Add on the
  // exact type it would create. Empty until /api/config/projects resolves.
  const [permByType, setPermByType] = useState({});
  // Same shape for the Delete letter 'D' — gates deleting a trace from the Graph view.
  const [delPermByType, setDelPermByType] = useState({});
  // False until /api/config/projects has resolved. ＋ Add stays HIDDEN while false — so the
  // button never flashes in (fail-open default) and then vanishes once per-type permissions
  // land, a window in which a fast click could open the add modal for a forbidden type.
  const [permReady, setPermReady] = useState(false);
  const [levelCard, setLevelCard] = useState(null);  // { level, x, y } — grid header info card
  const levelCardTimer = useRef(null);               // delayed hide, so the mouse can reach the card
  const [orcanosUrl, setOrcanosUrl] = useState('');  // tenant base URL, for the card's ↗ filter link
  const { filterCache, ensureFilters } = useFilterCache();   // filter names for the level card
  const [recentlyAddedIds, setRecentlyAddedIds] = useState(() => new Set()); // flash newly-added items
  const [reconciling, setReconciling] = useState(false); // silent post-add refresh (grid stays visible)
  const [pendingTraces, setPendingTraces] = useState([]);
  const [toast, setToast] = useState(null);          // { msg, kind: 'ok'|'warn' }
  // Hover info card: full item details fetched via QW_Get_Object on hover.
  const [itemCard, setItemCard] = useState(null);    // { id, x, y, loading, error, data }
  const [addMenu, setAddMenu] = useState(null);      // { x, y, options:[{blk,label}], onPick } — single ＋ opens this when a depth has >1 type
  const cardCacheRef = useRef({});                    // id -> card payload (avoid refetch)
  const cardTimerRef = useRef(null);                  // debounce hover -> fetch
  const cardHideTimerRef = useRef(null);              // small delay before hiding
  // Orcanos permission gates (from /api/auth/status). Default allowed so a slow/failed
  // status fetch never hides features; the backend enforces regardless.
  const [canAdd, setCanAdd] = useState(true);            // 'A' — inline add-item / drag-to-trace
  const [canAskPaul, setCanAskPaul] = useState(true);    // 'O' — ✨ Generate with AI
  // Orcanos dashboard-add right — gates ✎ Edit Panel. Read-only (viewer) licence => false.
  const [canManagePanels, setCanManagePanels] = useState(true);
  const toastTimer = useRef(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [backgroundExportProgress, setBackgroundExportProgress] = useState(null); // null = no export, 0-100 = progress
  const [currentExportJobId, setCurrentExportJobId] = useState(() => {
    try {
      return localStorage.getItem('currentExportJobId');
    } catch {
      return null;
    }
  });
  const [showExportCancelMenu, setShowExportCancelMenu] = useState(false);
  const showExportModalRef = useRef(false);
  // Same overlapping-poll hazard as ExportModal: skip a tick while the previous one is out,
  // and hand a given job's file to the browser at most once.
  const exportPollBusyRef = useRef(false);
  const downloadedJobsRef = useRef(new Set());
  // Jobs THIS page started. A job id also survives in localStorage, so without this a
  // completed job re-downloaded itself on every reload / re-entry to the panel.
  const sessionExportJobsRef = useRef(new Set());

  // Sticky header has two stacked rows: the level-name row (which also hosts the
  // matrix actions) and the column-name row. The column-name row must stick right
  // below the level-name row, so we measure its height rather than hardcoding it.
  const groupHeadRef = useRef(null);
  const [colHeadTop, setColHeadTop] = useState(0);     // = level-name row height

  // The actions (＋ Add / Expand / Collapse) float at the right edge of the L1
  // header cell. When that lane is narrow they ride over the level name, so the
  // Add button drops its type suffix ("＋ Add User Requirement" → "＋ Add").
  const l1HeadRef = useRef(null);
  const actionsRef = useRef(null);
  const addTypeRef = useRef(null);
  const l1LabelRef = useRef(null);
  const [compactAdd, setCompactAdd] = useState(false);

  // Feature 3: Toggle "Show Traced Items Info" with sessionStorage persistence.
  // Off by default — the column is noisy and duplicates what the grid already
  // shows; it's opt-in from the ⋮ menu.
  const [showTracedItems, setShowTracedItems] = useState(() => {
    try {
      const stored = sessionStorage.getItem('showTracedItems');
      return stored !== null ? JSON.parse(stored) : false;
    } catch (e) {
      // sessionStorage unavailable (incognito, disabled, etc)
      console.warn('sessionStorage unavailable:', e);
      return false;
    }
  });

  // Wrap long cell text. Off by default: every cell is clamped to ONE line (with an
  // ellipsis) so rows keep an even height. On: the cell wraps inside the same max
  // width and the row grows to the full height of its tallest cell. Either way the
  // column can never exceed --matrix-cell-max, so a long Description no longer
  // pushes the rest of the grid off to the right. Persisted like rowsPerPage.
  const [wrapText, setWrapText] = useState(() => {
    try {
      const stored = localStorage.getItem('matrixWrapText');
      return stored !== null ? JSON.parse(stored) : false;
    } catch (e) {
      console.warn('localStorage unavailable:', e);
      return false;
    }
  });

  // Fit to screen: a compact grid (smaller type, tight padding, text wrapped to at
  // most 3 lines) whose column cap is computed from the grid's width so the whole
  // trace fits across without side-scrolling. Display only; persisted per browser.
  const [fitScreen, setFitScreen] = useState(() => {
    try { return localStorage.getItem('matrixFitScreen') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('matrixFitScreen', fitScreen ? '1' : '0'); } catch (e) { /* ignore */ }
  }, [fitScreen]);
  const [gridWidth, setGridWidth] = useState(0);
  const gridRoRef = useRef(null);
  // Callback ref: the container mounts/unmounts with the view, so observe it as it comes
  const gridContainerRef = useCallback((el) => {
    if (gridRoRef.current) { gridRoRef.current.disconnect(); gridRoRef.current = null; }
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => setGridWidth(Math.round(entry.contentRect.width)));
    ro.observe(el);
    gridRoRef.current = ro;
  }, []);

  // Column order. ON (default): the columns render in the order the Orcanos filter
  // defines them. OFF: the legacy layout, which force-pushed "Last Test Run Result",
  // "Last Test Run Version" and "Traced Items Info" to the end of every level no
  // matter where the filter put them. Persisted like wrapText; also sent to the
  // exports so a downloaded file matches what's on screen.
  const [keepFilterOrder, setKeepFilterOrder] = useState(() => {
    try {
      const stored = localStorage.getItem('matrixKeepFilterOrder');
      return stored !== null ? JSON.parse(stored) : true;
    } catch (e) {
      console.warn('localStorage unavailable:', e);
      return true;
    }
  });

  // Filter bar (status pills + column options) behind the funnel icon next to search, and
  // the chain panel on the right — both hidden until the user opens them (2026-10-27).
  const [showFilterBar, setShowFilterBar] = useState(() => readPref('matrixFilterBar', false));
  const [showChain, setShowChain] = useState(() => readPref('matrixChainPanel', false));
  const [statusFilter, setStatusFilter] = useState('all');   // all | gap | notexec | ok
  const [chainL1, setChainL1] = useState(null);               // L1 id shown in the chain panel
  const [showColumnsMenu, setShowColumnsMenu] = useState(false);
  // Panel-wide coverage per level for the header bars — the Traceability Dashboard's summary
  // (written by the funnel build, Note #108). null = not built yet: the headers show no bars.
  const [coverage, setCoverage] = useState(null);
  const [chainCollapsed, setChainCollapsed] = useState(() => new Set());   // `${l1Id}${path}` of folded branches
  const [chainWidth, setChainWidth] = useState(() => readPref('matrixChainWidth', 380));   // px, dragged from its left edge

  // Feature 4: Collapse/Expand L1 rows with sessionStorage persistence
  const [expandedL1Items, setExpandedL1Items] = useState(() => {
    try {
      const stored = sessionStorage.getItem('expandedL1Items');
      if (stored) {
        return new Set(JSON.parse(stored));
      }
    } catch (e) {
      // sessionStorage unavailable or quota exceeded - fall back to default
      console.warn('sessionStorage unavailable, collapse state will not persist:', e);
    }
    return new Set(); // Default to all collapsed
  });

  const navigate = useNavigate();
  // Opened from the Risk home (a linked Traceability panel) → Back returns there.
  const location = useLocation();
  const backTo = location.state?.from || '/trace/dashboard';

  // ---- Funnel view (Matrix ⇄ Funnel toggle) ----
  // Restore the last view so an F5 keeps you on Matrix / Funnel / Graph.
  const [viewMode, setViewMode] = useState(() => {
    try {
      const v = sessionStorage.getItem('matrixViewMode');
      return v === 'funnel' || v === 'graph' || v === 'dashboard' ? v : 'matrix';
    } catch {
      return 'matrix';
    }
  }); // 'matrix' | 'funnel' | 'graph' | 'dashboard'
  // First-load guards so the reload hard-refresh only fires on the initial fetch
  const firstMatrixLoad = useRef(true);
  const firstGraphLoad = useRef(true);

  // Persist the active view so a reload lands back on it
  useEffect(() => {
    try { sessionStorage.setItem('matrixViewMode', viewMode); } catch (e) { /* ignore */ }
  }, [viewMode]);

  // Maximize mode: hide the app header, toolbar and footer so the working
  // area (matrix / funnel / graph) fills the viewport. The bars come back as
  // floating overlays on edge-hover (CSS-driven) — see .matrix-maximized in
  // MatrixPage.css. Persisted per session so a reload keeps the state.
  const [maximized, setMaximized] = useState(() => {
    try { return sessionStorage.getItem('matrixMaximized') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { sessionStorage.setItem('matrixMaximized', maximized ? '1' : '0'); } catch (e) { /* ignore */ }
    document.body.classList.toggle('matrix-maximized', maximized);
    return () => document.body.classList.remove('matrix-maximized');
  }, [maximized]);
  // Esc exits maximize — but not while typing in the search box (Esc there
  // clears the search), so ignore the key when it targets a form field.
  useEffect(() => {
    if (!maximized) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      const tag = (e.target && e.target.tagName) || '';
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(tag)) return;
      setMaximized(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [maximized]);
  const [funnel, setFunnel] = useState(null);          // { cached, funnel, built_at }
  const [funnelLoading, setFunnelLoading] = useState(false);
  const [funnelProgress, setFunnelProgress] = useState(null); // null | 0-100
  const [funnelMessage, setFunnelMessage] = useState('');
  const [funnelError, setFunnelError] = useState('');
  const funnelPollRef = useRef(null);

  // ---- Graph view (paged node-link diagram, N L1 chains per page) ----
  const [graphPage, setGraphPage] = useState(1);
  const [graphPageSize, setGraphPageSize] = useState(() => {
    const saved = parseInt(localStorage.getItem(GRAPH_PAGE_SIZE_KEY), 10);
    return GRAPH_PAGE_SIZE_OPTIONS.includes(saved) ? saved : 10;
  });
  const [graphRows, setGraphRows] = useState([]);
  const [graphLevels, setGraphLevels] = useState([]);
  const [graphTotalPages, setGraphTotalPages] = useState(1);
  const [graphLoading, setGraphLoading] = useState(false);
  const [graphError, setGraphError] = useState('');
  const [graphCachedAt, setGraphCachedAt] = useState(null);

  const fetchGraph = async (refresh = false) => {
    // On an F5 reload while Graph is the active view, force a hard refresh from Orcanos
    const hard = refresh || (firstGraphLoad.current && viewMode === 'graph' && takeReload());
    firstGraphLoad.current = false;
    setGraphLoading(true);
    setGraphError('');
    try {
      const response = await fetch(`/api/matrix/execute`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
        body: JSON.stringify({ setup_id: setupId, page: graphPage, page_size: graphPageSize, refresh: hard, search: search || undefined }),
      });
      const data = await response.json();
      if (data.IsSuccess) {
        setGraphRows(data.rows || []);
        setGraphLevels(data.configured_levels || []);
        setGraphTotalPages(data.total_pages || 1);
        setGraphCachedAt(data.from_cache ? data.cached_at : null);
      } else {
        setGraphError(data.error || 'Failed to load graph');
      }
      return data;
    } catch (err) {
      setGraphError('Error: ' + err.message);
      return null;
    } finally {
      setGraphLoading(false);
    }
  };

  // Fetch the graph page whenever the Graph view is open and its page changes
  useEffect(() => {
    if (viewMode === 'graph') fetchGraph();
  }, [viewMode, graphPage, graphPageSize, search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Create a trace between two existing items (drag-to-trace in the graph).
  // Always deeper -> shallower; returns { ok, error } to the GraphView.
  const createRelation = async ({ sourceKey, targetKey, sourceLevel, targetLevel }) => {
    try {
      const resp = await fetch(`/api/matrix/${setupId}/add-relation`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
        body: JSON.stringify({
          source_key: sourceKey, target_key: targetKey,
          source_level: sourceLevel, target_level: targetLevel,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data.IsSuccess && data.status === 'linked') return { ok: true };
      return { ok: false, error: data.error || data.detail || `Link failed (${resp.status})` };
    } catch (err) {
      return { ok: false, error: err.message };
    }
  };

  // Delete a trace selected in the graph (QW_Delete_Relation). Same body as createRelation:
  // source = deeper, target = shallower. Returns { ok, error } to the GraphView.
  const deleteRelation = async ({ sourceKey, targetKey, sourceLevel, targetLevel }) => {
    try {
      const resp = await fetch(`/api/matrix/${setupId}/delete-relation`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
        body: JSON.stringify({
          source_key: sourceKey, target_key: targetKey,
          source_level: sourceLevel, target_level: targetLevel,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data.IsSuccess && data.status === 'deleted') return { ok: true };
      return { ok: false, error: data.error || data.detail || `Delete failed (${resp.status})` };
    } catch (err) {
      return { ok: false, error: err.message };
    }
  };

  // Orphan Mode: paginated fetch of a level's untraced items (from its orphan filter).
  const fetchOrphans = async (level, offset = 0, limit = 25) => {
    try {
      const q = new URLSearchParams({ level, offset, limit });
      const resp = await fetch(`/api/matrix/${setupId}/orphans?${q}`, { credentials: 'include' });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data.IsSuccess) {
        return { ok: true, orphans: data.orphans || [], hasMore: data.has_more, total: data.total, configured: data.configured };
      }
      return { ok: false, error: data.detail || data.error || `Failed (${resp.status})` };
    } catch (err) {
      return { ok: false, error: err.message };
    }
  };

  // Orphan Mode: search a level's items (its main filter) to find any parent across the panel.
  const fetchParents = async (level, searchTerm = '', offset = 0, limit = 25) => {
    try {
      const q = new URLSearchParams({ level, offset, limit });
      if (searchTerm) q.set('search', searchTerm);
      const resp = await fetch(`/api/matrix/${setupId}/level-items?${q}`, { credentials: 'include' });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data.IsSuccess) {
        return { ok: true, items: data.items || [], hasMore: data.has_more, total: data.total };
      }
      return { ok: false, error: data.detail || data.error || `Failed (${resp.status})` };
    } catch (err) {
      return { ok: false, error: err.message };
    }
  };

  // Graph "Related Items": one hop of an item's relations, incl. items outside the panel
  // filter scope. Returns { ok, nodes, links } — nodes are new external items to draw,
  // links are all neighbour edges (existing:true = already on the graph → dashed cycle).
  const fetchRelations = async ({ itemId, linkId = '', exclude = '', excludeKeys = '' }) => {
    try {
      // POST (not GET): exclude/exclude_keys carry every id+key already on the graph, which
      // overflows the reverse-proxy query-string limit on large panels (bare 404 in prod).
      const resp = await fetch(`/api/matrix/${setupId}/relations`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
        body: JSON.stringify({
          item_id: itemId,
          link_id: linkId || '',
          exclude: exclude || '',
          exclude_keys: excludeKeys || '',
        }),
      });
      const data = await resp.json().catch(() => null);
      if (resp.ok && data && data.IsSuccess) {
        return { ok: true, nodes: data.nodes || [], links: data.links || [] };
      }
      return { ok: false, error: readError(data, resp, 'load related items') };
    } catch (err) {
      return { ok: false, error: `Could not load related items: ${err.message}` };
    }
  };

  // Hover info card: fetch a single item's full details via QW_Get_Object.
  const fetchItemCard = async (itemId, linkId = '') => {
    try {
      const q = new URLSearchParams({ item_id: itemId });
      if (linkId) q.set('link_id', linkId);
      const resp = await fetch(`/api/matrix/${setupId}/item-card?${q}`, { credentials: 'include' });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data.IsSuccess) return { ok: true, data };
      return { ok: false, error: data.detail || data.error || `Failed (${resp.status})` };
    } catch (err) {
      return { ok: false, error: err.message };
    }
  };

  // Show the card after a short hover on the info icon; fetch (and memoize) the details
  // if not cached. Anchored to the icon (not the cursor), so it doesn't follow the mouse.
  const showItemCard = (e, item) => {
    if (!item || !item.id) return;
    clearTimeout(cardHideTimerRef.current);
    clearTimeout(cardTimerRef.current);
    const rect = e.currentTarget.getBoundingClientRect();
    const x = rect.right;
    const y = rect.bottom;
    const url = item.url || '';
    cardTimerRef.current = setTimeout(async () => {
      const cached = cardCacheRef.current[item.id];
      if (cached) {
        setItemCard({ id: item.id, x, y, url, loading: false, error: '', data: cached });
        return;
      }
      setItemCard({ id: item.id, x, y, url, loading: true, error: '', data: null });
      const linkId = url ? (url.match(/ItemId=(\d+)/)?.[1] || '') : '';
      const res = await fetchItemCard(item.id, linkId);
      if (res.ok) {
        cardCacheRef.current[item.id] = res.data;
        setItemCard((c) => (c && c.id === item.id ? { ...c, loading: false, data: res.data } : c));
      } else {
        setItemCard((c) => (c && c.id === item.id ? { ...c, loading: false, error: res.error } : c));
      }
    }, 250);
  };

  // Keep the card open while the pointer is on it (moving from icon → card).
  const keepItemCard = () => {
    clearTimeout(cardHideTimerRef.current);
  };

  const hideItemCard = () => {
    clearTimeout(cardTimerRef.current);
    cardHideTimerRef.current = setTimeout(() => setItemCard(null), 220);
  };

  // `afterBuild`: a build just ended — if there is still no snapshot, say so instead of
  // leaving "No snapshot yet" on screen as if the click did nothing.
  const loadFunnel = async (afterBuild = '') => {
    setFunnelLoading(true);
    setFunnelError('');
    try {
      const resp = await fetch(`/api/matrix/${setupId}/funnel`, { credentials: 'include' });
      if (resp.ok) {
        const data = await resp.json();
        setFunnel(data);
        if (afterBuild && !data.cached) setFunnelError(afterBuild);
      } else {
        setFunnelError('Failed to load funnel');
      }
    } catch (err) {
      setFunnelError('Error: ' + err.message);
    } finally {
      setFunnelLoading(false);
    }
  };

  // A failed poll used to stop the poll silently and never reload: the build finished, the
  // snapshot was saved, and the page still said "No funnel data yet". One bad poll on a busy
  // server is not the end of the build — retry, and whenever polling stops, re-read the cache.
  const pollFunnelJob = (jobId) => {
    if (funnelPollRef.current) clearInterval(funnelPollRef.current);
    let misses = 0;
    const giveUp = () => {
      clearInterval(funnelPollRef.current);
      setFunnelProgress(null);
      setFunnelMessage('');
      loadFunnel('Lost track of the build (the server stopped answering about it). Click Build funnel to try again.');
    };
    funnelPollRef.current = setInterval(async () => {
      try {
        const resp = await fetch(`/api/matrix/export-jobs/${jobId}`, { credentials: 'include' });
        if (!resp.ok) {
          if (resp.status === 404 || ++misses >= 8) giveUp();
          return;
        }
        misses = 0;
        const job = await resp.json();
        setFunnelProgress(job.progress || 0);
        setFunnelMessage(job.message || '');
        if (job.status === 'completed') {
          clearInterval(funnelPollRef.current);
          setFunnelProgress(null);
          setFunnelMessage('');
          loadFunnel('The build finished but saved no snapshot. Click Build funnel to try again.');
        } else if (job.status === 'error') {
          clearInterval(funnelPollRef.current);
          setFunnelProgress(null);
          setFunnelError(job.error || 'Build failed');
          loadFunnel();
        }
      } catch (err) {
        if (++misses >= 8) giveUp();
      }
    }, 700);
  };

  /**
   * Rebuild the funnel.
   *
   * `mode: 'smart'` re-reads only the rows Orcanos says changed since the last build and
   * merges them onto the stored corpus — seconds instead of minutes on a large panel. It
   * escalates to a full read on its own when the incremental answer can't be trusted (a
   * level whose filter has no Updated Date column, or a count that says something was
   * deleted), and the job's own progress line says so when it does.
   *
   * Anything other than the string 'smart' is a full rebuild, so a handler that passes a
   * click event can never accidentally take the cheap path.
   */
  const refreshFunnel = async (mode) => {
    const smart = mode === 'smart';
    setFunnelError('');
    // The POST first reads level 1 from Orcanos to size the build — seconds on a big filter,
    // with nothing on screen. Show the progress bar from the click, not from the 202.
    setFunnelProgress(0);
    setFunnelMessage('Starting — checking level 1 in Orcanos...');
    try {
      const resp = await fetch(
        `/api/matrix/${setupId}/funnel/${smart ? 'smart-refresh' : 'refresh'}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-CSRF-Token': getCsrfToken() },
      });
      if (resp.status === 202) {
        const j = await resp.json();
        setFunnelProgress(0);
        setFunnelMessage(j.message || 'Building...');
        pollFunnelJob(j.job_id);
      } else {
        const e = await resp.json().catch(() => ({}));
        setFunnelProgress(null);
        setFunnelMessage('');
        setFunnelError(e.detail || `Failed to start build (${resp.status})`);
      }
    } catch (err) {
      setFunnelProgress(null);
      setFunnelMessage('');
      setFunnelError('Error: ' + err.message);
    }
  };

  // Load the cached funnel the first time the Funnel view is opened
  useEffect(() => {
    if (viewMode === 'funnel' && funnel === null && !funnelLoading) {
      loadFunnel();
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stop polling on unmount
  useEffect(() => () => { if (funnelPollRef.current) clearInterval(funnelPollRef.current); }, []);

  // Re-attach to a funnel build that is STILL RUNNING (v3.31.1).
  //
  // The build is a server thread and never cared that the page went away — but the poll
  // above lives in this component, so leaving the matrix mid-build and coming back through
  // Full View left the job unwatched: stale funnel, no progress bar, exactly what "it
  // stopped" looks like. The kind check matters — an HTML export of this same panel is a
  // download, not a funnel build. A failure here costs the progress bar and nothing else.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`/api/matrix/${setupId}/active-job`, { credentials: 'include' });
        if (!r.ok) return;
        const { job } = await r.json();
        if (cancelled || !job || job.status !== 'processing') return;
        if (job.kind !== 'funnel_build' && job.kind !== 'funnel_smart') return;
        setFunnelProgress(job.progress || 0);
        setFunnelMessage(job.message || 'Building...');
        pollFunnelJob(job.job_id);
      } catch (e) { /* no progress bar is the only cost */ }
    })();
    return () => { cancelled = true; };
  }, [setupId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fixed-layout mode: header + toolbar pinned top, footer pinned bottom,
  // only the grid scrolls. Scoped to this page via a body class so other
  // pages keep their normal document scrolling.
  useEffect(() => {
    document.body.classList.add('matrix-fullscreen');
    return () => document.body.classList.remove('matrix-fullscreen');
  }, []);

  // Clear hover-card timers on unmount so they don't fire after teardown.
  useEffect(() => () => {
    clearTimeout(cardTimerRef.current);
    clearTimeout(cardHideTimerRef.current);
  }, []);

  // Keep ref in sync so background polling can check modal state without stale closure
  useEffect(() => {
    showExportModalRef.current = showExportModal;
  }, [showExportModal]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`/api/matrix/${setupId}/dashboard`, { credentials: 'include' });
        if (!resp.ok) return;
        const data = await resp.json();
        if (!cancelled) setCoverage(data.cached && data.summary ? { ...data.summary, built_at: data.built_at } : null);
      } catch { /* no bars — the grid works without them */ }
    })();
    return () => { cancelled = true; };
  }, [setupId, funnel?.built_at]);

  // Measure the level-name row (which now also hosts the actions) so the
  // column-name row sticks right beneath it (level names → column names)
  useEffect(() => {
    const update = () => {
      const gH = groupHeadRef.current ? groupHeadRef.current.getBoundingClientRect().height : 0;
      setColHeadTop(gH);
    };
    update();
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      if (groupHeadRef.current) ro.observe(groupHeadRef.current);
    }
    window.addEventListener('resize', update);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [configuredLevels, rows, showTracedItems]);

  // Smart resize of the Add button: the actions float over the L1 header lane, so
  // when that lane is too narrow to hold both the level name and the actions, the
  // Add button sheds its type suffix. The suffix span keeps its content width even
  // while collapsed (max-width:0 + overflow:hidden ⇒ scrollWidth is still the full
  // text), so the *uncompacted* width is measurable in either state — no flip-flop.
  useEffect(() => {
    const MIN_GAP = 12;
    const update = () => {
      const strip = actionsRef.current, label = l1LabelRef.current;
      if (!strip || !label) return;
      const typeEl = addTypeRef.current;
      const typeW = typeEl ? typeEl.scrollWidth + 4 : 0;   // full suffix text + its margin-left
      // Measure the REAL rendered positions rather than deriving from the L1
      // <th>'s clientWidth — a colSpan cell reports the whole span's width, not
      // where the pinned-right actions strip actually sits, so the arithmetic
      // proxy over-counted free space and never compacted (the Add button rode
      // over the level name). The strip is pinned to the right of the L1 lane,
      // so its right edge is fixed; overlap = label's right edge crossing the
      // strip's (uncompacted) left edge. Always compare against the uncompacted
      // width — measurable even while collapsed (max-width:0 keeps scrollWidth =
      // full text) — so the decision is stable and can't flip-flop.
      const stripRect = strip.getBoundingClientRect();
      const labelRect = label.getBoundingClientRect();
      // The coverage bar sits right of the name — the Add button must not cover it either.
      const cov = label.parentElement && label.parentElement.querySelector('.lvl-cov');
      const contentRight = cov ? Math.max(labelRect.right, cov.getBoundingClientRect().right) : labelRect.right;
      setCompactAdd(prev => {
        const uncompactedW = stripRect.width + (prev ? typeW : 0);
        const uncompactedLeft = stripRect.right - uncompactedW;
        return contentRight + MIN_GAP > uncompactedLeft;
      });
    };
    update();
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      if (l1HeadRef.current) ro.observe(l1HeadRef.current);
    }
    window.addEventListener('resize', update);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [configuredLevels, rows, showTracedItems, canAdd, coverage]);

  // Persist currentExportJobId to localStorage
  useEffect(() => {
    try {
      if (currentExportJobId) {
        localStorage.setItem('currentExportJobId', currentExportJobId);
      } else {
        localStorage.removeItem('currentExportJobId');
      }
    } catch (e) {
      console.warn('localStorage unavailable:', e);
    }
  }, [currentExportJobId]);

  // Background export polling: tracks progress in toolbar and triggers download when modal is closed
  useEffect(() => {
    if (currentExportJobId && backgroundExportProgress === null) {
      const jobId = currentExportJobId;
      let stopped = false;
      const pollInterval = setInterval(async () => {
        if (stopped || exportPollBusyRef.current) return;
        exportPollBusyRef.current = true;
        try {
          const response = await fetch(`/api/matrix/export-jobs/${jobId}`, {
            credentials: 'include'
          });
          if (response.ok) {
            const job = await response.json();
            setBackgroundExportProgress(job.progress || 0);

            if (job.status === 'completed') {
              stopped = true;
              clearInterval(pollInterval);
              setBackgroundExportProgress(null);
              setCurrentExportJobId(null);

              // Download here only when the modal is closed (it does its own), the job was
              // started by THIS page (not a stale id restored from localStorage), and it
              // hasn't already been handed over.
              if (!showExportModalRef.current
                  && sessionExportJobsRef.current.has(jobId)
                  && !downloadedJobsRef.current.has(jobId)) {
                downloadedJobsRef.current.add(jobId);
                try {
                  const dlResponse = await fetch(`/api/matrix/export-jobs/${jobId}/download`, {
                    credentials: 'include'
                  });
                  if (dlResponse.ok) {
                    const blob = await dlResponse.blob();
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = job.filename || `export-${jobId}.html`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                  }
                } catch (dlErr) {
                  console.error('Background auto-download failed:', dlErr);
                }
              }
            } else if (job.status === 'error') {
              stopped = true;
              clearInterval(pollInterval);
              setBackgroundExportProgress(null);
              setCurrentExportJobId(null);
            }
          } else if (response.status === 404) {
            stopped = true;
            clearInterval(pollInterval);
            setCurrentExportJobId(null);
            setBackgroundExportProgress(null);
          }
        } catch (err) {
          console.error('Failed to restore export state:', err);
        } finally {
          exportPollBusyRef.current = false;
        }
      }, 500);

      return () => { stopped = true; clearInterval(pollInterval); };
    }
  }, [currentExportJobId]);

  // Reset page to 1 when rowsPerPage changes, which triggers fetchMatrix via the next effect
  useEffect(() => {
    setPage(1);
  }, [rowsPerPage]);

  // Persist rowsPerPage to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('matrixRowsPerPage', String(rowsPerPage));
    } catch (e) {
      console.warn('localStorage unavailable:', e);
    }
  }, [rowsPerPage]);

  useEffect(() => {
    fetchMatrix();
  }, [setupId, page, search]);

  // Feature 3: Persist showTracedItems to sessionStorage with error handling
  useEffect(() => {
    try {
      sessionStorage.setItem('showTracedItems', JSON.stringify(showTracedItems));
    } catch (e) {
      // Silently fail if quota exceeded - just don't persist
      console.warn('sessionStorage quota exceeded:', e);
    }
  }, [showTracedItems]);

  // Persist the wrap-text preference across sessions (a display preference, like rowsPerPage)
  useEffect(() => {
    try {
      localStorage.setItem('matrixWrapText', JSON.stringify(wrapText));
    } catch (e) {
      console.warn('localStorage unavailable, wrap preference not saved:', e);
    }
  }, [wrapText]);

  // Persist the column-order preference across sessions (a display preference, like wrapText)
  useEffect(() => {
    try {
      localStorage.setItem('matrixKeepFilterOrder', JSON.stringify(keepFilterOrder));
    } catch (e) {
      console.warn('localStorage unavailable, column-order preference not saved:', e);
    }
  }, [keepFilterOrder]);

  // The filter bar and the chain panel are shown/hidden per browser.
  useEffect(() => {
    try {
      localStorage.setItem('matrixFilterBar', JSON.stringify(showFilterBar));
      localStorage.setItem('matrixChainPanel', JSON.stringify(showChain));
      localStorage.setItem('matrixChainWidth', JSON.stringify(chainWidth));
    } catch { /* not persisted — the toggles still work */ }
  }, [showFilterBar, showChain, chainWidth]);

  // What "Share this view" attaches when this page is the one on screen. Registered
  // rather than pushed on every change: this component re-renders constantly while the
  // grid is being worked, and the dock only needs the answer at the moment somebody
  // clicks the chip.
  useEffect(() => registerViewState(() => ({
    page,
    expanded: [...expandedL1Items],
  })), [page, expandedL1Items]);

  // Scroll a shared row into view once it renders. Deliberately does NOT clear the
  // highlight on a timer: somebody who followed a link and then looked away needs the
  // answer to "which row did they mean?" to still be on the screen when they look back.
  const focusRowRef = useCallback((node) => {
    if (node) node.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, []);

  // ...but it IS cleared by the first deliberate interaction: once they have clicked or
  // typed on the grid they have found the row, and a highlight that outlives that reads
  // as a stuck selection on every later page.
  //
  // Three things this must not mistake for "found it":
  //  - the click that BROUGHT them here. The chat dock's card navigates, and the effect
  //    that sets `focusKey` runs while that click is still bubbling, so the listener is
  //    armed a tick later.
  //  - reading the conversation. Anything inside the dock is the sender's message, not
  //    the grid — clicking it must leave the answer on screen.
  //  - the smooth scroll above. Scroll and mousemove are not interaction here.
  useEffect(() => {
    if (!focusKey) return undefined;
    let armed = false;
    const arm = setTimeout(() => { armed = true; }, 0);
    const onInteract = (e) => {
      if (!armed) return;
      if (e.target?.closest && e.target.closest('.collab-dock, .collab-row-menu')) return;
      setFocusKey('');
    };
    document.addEventListener('click', onInteract, true);
    document.addEventListener('keydown', onInteract, true);
    return () => {
      clearTimeout(arm);
      document.removeEventListener('click', onInteract, true);
      document.removeEventListener('keydown', onInteract, true);
    };
  }, [focusKey]);

  /** Right-click a row → discuss it with a colleague.
   *
   *  The level comes from the CELL that was clicked (`data-level` on every `<td>`), not from
   *  the row. Taking the deepest item on the row meant every share came out as the Test Case
   *  however far left you clicked — which is wrong whenever the thing you want to ask about
   *  is the requirement.
   *
   *  A cell with nothing in it (a gap, or a level de-duplicated away on this row) falls back
   *  to the nearest item ABOVE it, then to the deepest on the row — there is always something
   *  to point at, and pointing at the nearest ancestor is the honest answer for an empty cell.
   */
  const openDiscuss = useCallback((e, row) => {
    const cell = e.target.closest ? e.target.closest('td[data-level]') : null;
    const clicked = cell ? Number(cell.dataset.level) : NaN;

    let picked = null;
    if (Number.isFinite(clicked)) {
      const idx = configuredLevels.findIndex((l) => l.level_num === clicked);
      for (let i = idx; i >= 0; i -= 1) {
        const item = row[`l${configuredLevels[i].level_num}`];
        if (item) { picked = { item, level: configuredLevels[i].level_num }; break; }
      }
    }
    const deepest = picked || deepestOnRow(row, configuredLevels);
    if (!deepest) return;
    e.preventDefault();
    const key = itemKeyOf(deepest.item);
    setDiscussAt({
      x: e.clientX,
      y: e.clientY,
      context: {
        kind: 'row',
        module: 'trace',
        route: `/trace/matrix/${setupId}`,
        panel_id: setupId,
        state: { page, expanded: [...expandedL1Items] },
        focus: { level: deepest.level, key, item_id: deepest.item.id },
        label: `L${deepest.level} · ${deepest.item.name || key}`.slice(0, 200),
      },
    });
  }, [configuredLevels, setupId, page, expandedL1Items]);

  // Somebody shared this view. `?ctx=` carries the page, the expanded branches and the row
  // they meant.
  //
  // ⚠️ Keyed on the ctx VALUE, not on mount. React Router keeps this component mounted when
  // only the params change, so a colleague who was already looking at a matrix — the common
  // case, since that is where you are when someone shares one — navigated to the shared URL
  // and nothing happened at all: no page change, no highlight. A mount-only effect cannot see
  // a navigation that does not remount.
  //
  // `appliedCtxRef` stops it re-applying the same card twice (a re-render, or a return to this
  // route), which would otherwise yank the user back to the sender's page after they had
  // paged away.
  const appliedCtxRef = useRef('');
  const rawCtx = searchParams.get('ctx') || '';
  useEffect(() => {
    if (!rawCtx || appliedCtxRef.current === rawCtx) return;
    const ctx = decodeContext(rawCtx);
    if (!ctx) return;
    appliedCtxRef.current = rawCtx;
    const state = ctx.state || {};
    if (state.page) setPage(Number(state.page) || 1);
    if (Array.isArray(state.expanded) && state.expanded.length) {
      setExpandedL1Items(new Set(state.expanded));
    }
    setFocusKey(ctx.focus?.key ? String(ctx.focus.key) : '');
  }, [rawCtx]);

  // Feature 4: Persist expandedL1Items to sessionStorage with error handling
  useEffect(() => {
    try {
      sessionStorage.setItem('expandedL1Items', JSON.stringify([...expandedL1Items]));
    } catch (e) {
      // Quota exceeded - silently continue without persisting
      console.warn('sessionStorage quota exceeded, collapse state not saved:', e);
    }
  }, [expandedL1Items]);

  // `silent` reconciles in the background without blanking the grid with the
  // full-page "Loading…" screen (used after an inline add). Returns the data.
  const fetchMatrix = async (refresh = false, silent = false) => {
    // On an F5 reload while Matrix is the active view, force a hard refresh from Orcanos
    const hard = refresh || (firstMatrixLoad.current && viewMode === 'matrix' && takeReload());
    firstMatrixLoad.current = false;
    if (silent) setReconciling(true); else setLoading(true);
    try {
      const response = await fetch(`/api/matrix/execute`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken()
        },
        body: JSON.stringify({ setup_id: setupId, page, page_size: rowsPerPage, refresh: hard, search: search || undefined })
      });

      const data = await response.json();
      if (data.IsSuccess) {
        setRows(data.rows || []);
        setConfiguredLevels(data.configured_levels || []);
        setTotalPages(data.total_pages || 1);
        setMatrixCachedAt(data.from_cache ? data.cached_at : null);
        setError('');
        return data;
      } else {
        setError(data.error || 'Failed to load matrix');
      }
    } catch (err) {
      console.error('Error fetching matrix:', err);
      setError('Error: ' + err.message);
    } finally {
      if (silent) setReconciling(false); else setLoading(false);
    }
    return null;
  };

  // ---- Inline add-item + trace ----

  const showToast = (msg, kind = 'ok', durationMs) => {
    setToast({ msg, kind });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    // Errors stay long enough to read (and are click-to-dismiss); success is brief
    toastTimer.current = setTimeout(() => setToast(null), durationMs || (kind === 'warn' ? 12000 : 3200));
  };
  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  const loadPendingTraces = async () => {
    try {
      const resp = await fetch(`/api/matrix/${setupId}/pending-traces`, { credentials: 'include' });
      if (resp.ok) {
        const data = await resp.json();
        setPendingTraces(data.pending || []);
      }
    } catch (err) {
      console.warn('Could not load pending traces:', err);
    }
  };
  useEffect(() => { loadPendingTraces(); }, [setupId]); // eslint-disable-line react-hooks/exhaustive-deps

  // The panel's name — the matrix response doesn't carry it, so read it from the setup.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`/api/config/${setupId}`, { credentials: 'include' });
        if (!resp.ok) return;
        const data = await resp.json();
        if (!cancelled && data?.name) setPanelName(data.name);
      } catch (err) {
        console.warn('Could not load panel name:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [setupId]);

  // Keep the browser tab title in sync so the panel is identifiable when tabbed away.
  useEffect(() => {
    if (!panelName) return;
    const prev = document.title;
    document.title = `${panelName} — Traceability Matrix`;
    return () => { document.title = prev; };
  }, [panelName]);

  // Load the user's Orcanos permission gates so we can hide affordances they can't use
  // (Add → inline add / drag-to-trace; Ask Paul → ✨ Generate with AI).
  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch('/api/auth/status', { credentials: 'include' });
        if (!resp.ok) return;
        const data = await resp.json();
        if (typeof data.can_add === 'boolean') setCanAdd(data.can_add);
        if (typeof data.can_ask_paul === 'boolean') setCanAskPaul(data.can_ask_paul);
        if (typeof data.can_manage_panels === 'boolean') setCanManagePanels(data.can_manage_panels);
      } catch { /* keep defaults (allowed) on any failure */ }
    })();
  }, []);

  // Load work-item type labels so the add button shows the type's real
  // description (e.g. "Product Requirement") rather than the user-chosen level name.
  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch('/api/config/projects', { credentials: 'include' });
        if (!resp.ok) { setPermReady(true); return; }
        const data = await resp.json();
        setOrcanosUrl(data.orcanos_url || '');
        const map = {};
        const byCombo = {};
        const byProject = {};
        const perms = {};
        const delPerms = {};
        (data.projects || []).forEach((p) => {
          const pid = p.project_id != null ? String(p.project_id) : null;
          (p.item_types || []).forEach((it) => {
            if (it.code && it.label) map[it.code] = it.label;
            // Per-(project, type) Add permission. can_add may be absent on an older backend —
            // only record it when the field is actually present so we can fall back cleanly.
            if (pid && it.code && typeof it.can_add === 'boolean') {
              (perms[pid] || (perms[pid] = {}))[it.code] = it.can_add;
            }
            if (pid && it.code && typeof it.can_delete === 'boolean') {
              (delPerms[pid] || (delPerms[pid] = {}))[it.code] = it.can_delete;
            }
          });
          const name = p.project_name || '';
          if (pid != null) byProject[pid] = name;
          if (pid != null && p.version_id != null) {
            byCombo[`${pid}|${p.version_id}`] = { name, version: p.version_label || '' };
          }
        });
        setTypeLabels(map);
        setProjectLookup({ byCombo, byProject });
        setPermByType(perms);
        setDelPermByType(delPerms);
      } catch (err) {
        console.warn('Could not load work-item type labels:', err);
      } finally {
        setPermReady(true);   // resolved (ok, not-ok, or error) — the ＋ gate can now decide
      }
    })();
  }, []);

  // "Cardiology Monitor (2.0)" for a block's project/version, falling back to the raw
  // ids when the projects list hasn't resolved (or the project is no longer visible).
  const resolveProjectLabel = (block) => {
    if (!block) return '';
    const combo = projectLookup.byCombo[`${block.project_id}|${block.version_id}`];
    const name = (combo && combo.name) || projectLookup.byProject[String(block.project_id)] || '';
    const version = (combo && combo.version) || (block.version_id != null ? `v${block.version_id}` : '');
    if (name && version) return `${name} (${version})`;
    if (name) return name;
    if (block.project_id == null) return '';
    return version ? `${block.project_id} (${version})` : String(block.project_id);
  };

  // The level header's ⓘ card: no fetch — everything is already in configured_levels.
  // Filter names come from the same GetFilterList cache as the wizard; until it answers
  // (or on a tenant without it) the card shows the id alone.
  const showLevelCard = (e, level) => {
    clearTimeout(levelCardTimer.current);
    const r = e.currentTarget.getBoundingClientRect();
    setLevelCard({ level, x: r.right, y: r.bottom + 6 });
    blocksOfLevel(level).forEach((b) => {
      if (b.project_id && b.work_item_type) ensureFilters(b.project_id, b.work_item_type, levelTypeLabel(b));
    });
  };
  const hideLevelCard = () => {
    clearTimeout(levelCardTimer.current);
    levelCardTimer.current = setTimeout(() => setLevelCard(null), 180);
  };
  const keepLevelCard = () => clearTimeout(levelCardTimer.current);
  const filterNameOf = (b) => {
    const entry = filterCache[`${b.project_id}|${b.work_item_type}|${levelTypeLabel(b) || ''}`];
    const f = entry && entry.filters && entry.filters.find((x) => String(x.id) === String(b.filter_id));
    return f ? f.name : '';
  };

  // The blocks (project + type + filter) that make up a level. A depth can hold several.
  const blocksOfLevel = (level) => (
    (Array.isArray(level?.types) && level.types.length) ? level.types : [level]
  );

  // Full type description for a level's work item (e.g. "Product Requirement").
  // Prefer the backend-resolved label, then the projects lookup, then the level
  // name, and only fall back to the raw code as a last resort.
  const levelTypeLabel = (level) => {
    if (!level) return '';
    return level.type_label || typeLabels[level.work_item_type] || level.name || level.work_item_type || '';
  };

  // Best-effort original-code prefix for a level: take it from any item already
  // shown at that level (e.g. "TC-8789 (7677)" -> "TC"). null when the level is
  // still empty everywhere on this page.
  const childKeyPrefixFor = (levelNum, sourceRows = rows) => {
    for (const row of sourceRows) {
      const item = row[`l${levelNum}`];
      const key = item && item.columns && item.columns.Key;
      if (key) {
        const token = String(key).trim().split(/[\s(]/)[0];
        const dash = token.lastIndexOf('-');
        if (dash > 0) return token.slice(0, dash);
      }
    }
    return null;
  };

  // A Test Run isn't creatable directly in Orcanos — it's produced by executing a Test
  // Case. When the target level is a Test Run, the add flow creates a Test Case instead
  // (the backend does the real substitution; this just keeps the modal wording honest).
  const isTestRunLevel = (level) => {
    const lbl = String(levelTypeLabel(level) || '').toLowerCase();
    const code = String((level && level.work_item_type) || '').toUpperCase();
    if (lbl.includes('test case')) return false;
    return lbl.includes('test run') || ['TEST_RUN', 'T_RUN', 'TRUN', 'TR', 'RUN'].includes(code);
  };

  // Test Case and Test Run share the code T_CASE, so the type LABEL decides; a block with
  // no stored label (legacy panel) reads as a plain Test Case.
  const isTestCaseLevel = (level) => {
    const lbl = String(levelTypeLabel(level) || '').toLowerCase();
    const code = String((level && level.work_item_type) || '').toUpperCase();
    if (lbl.includes('test run')) return false;
    if (lbl.includes('test case')) return true;
    return code === 'T_CASE';
  };

  // A Test Run is produced by EXECUTING its Test Case, not by authoring an item. Everywhere
  // else an un-creatable Test Run target is substituted with a Test Case — but on a Test
  // Case → Test Run hop that would create a SECOND Test Case under a Test Case, which isn't
  // the trace the panel describes. So there is nothing to add on that hop: no ＋ button (the
  // backend refuses it too).
  const canAddChildBlock = (parentBlock, childBlock) =>
    !(isTestRunLevel(childBlock) && isTestCaseLevel(parentBlock));

  // The specific block an item belongs to at its depth (a depth can mix several types).
  const blockOfItem = (level, item) => {
    if (!level) return null;
    const types = Array.isArray(level.types) ? level.types : [];
    if (item && item.block_id) {
      const own = types.find((b) => b && b.block_id === item.block_id);
      if (own) return own;
    }
    return types[0] || level;
  };

  // A depth can hold several block types. Resolve the specific child block to add: an
  // explicit `childBlock` (one of the depth's `types`) wins; otherwise fall back to a
  // synthetic block from the depth's first type so single-type levels behave as before.
  const childBlockOf = (childLevel, childBlock) => {
    if (childBlock) return childBlock;
    const t = Array.isArray(childLevel.types) && childLevel.types[0];
    return t || {
      block_id: undefined,
      work_item_type: childLevel.work_item_type,
      type_label: childLevel.type_label,
      name: childLevel.name,
    };
  };

  // Can the user ADD this block's work-item type in this block's project? Orcanos returns Add
  // PER type PER project (QW_Login Item_type[].Permission), so a global 'A' union is wrong — it
  // would show ＋ for a type the user can't add just because some OTHER type granted Add. When the
  // per-type permission isn't known yet (lookup still loading, or the pair is absent) fall back to
  // the coarse global `canAdd` so we never over-hide; the backend re-checks per type and 403s.
  const canAddBlockType = (block) => {
    // Hide by default until we actually know: no flash-in-then-remove, and no window where a
    // quick click opens the add modal for a type the user can't add. Once resolved, use the
    // exact type's permission; a type with no letters (absent field) or a failed load falls
    // back to the coarse global gate — the backend still 403s per type.
    if (!permReady) return false;
    // Account-level "may add items" gate (folded into canAdd by the backend). When an admin
    // unticks it on /admin, canAdd is false and NOTHING is addable — the coarse union of the
    // user's per-type Orcanos 'A' means any addable type also makes canAdd true, so this guard
    // only ever fires when add is genuinely off (never over-hides an otherwise-allowed type).
    if (!canAdd) return false;
    const code = block && block.work_item_type;
    const pid = block && block.project_id != null ? String(block.project_id) : null;
    if (pid && code) {
      const byCode = permByType[pid];
      if (byCode && Object.prototype.hasOwnProperty.call(byCode, code)) return !!byCode[code];
    }
    return canAdd;
  };

  // May the user DELETE a trace whose deeper item sits at this graph level? Gated like add-relation
  // (authored = deeper side), on the QW_Login Delete letter 'D' per type per project. A depth with
  // several types passes when ANY of them allows it; unknown letters fail open (backend re-checks).
  const canDeleteTraceAt = (levelIdx) => {
    if (!permReady || !canAdd) return false;   // canAdd also carries the account write gate
    const level = graphLevels[levelIdx];
    if (!level) return false;
    const blocks = (Array.isArray(level.types) && level.types.length) ? level.types : [level];
    return blocks.some((b) => {
      const pid = b && b.project_id != null ? String(b.project_id) : null;
      const byCode = pid && delPermByType[pid];
      if (byCode && b.work_item_type && Object.prototype.hasOwnProperty.call(byCode, b.work_item_type)) {
        return !!byCode[b.work_item_type];
      }
      return true;
    });
  };

  // Open the add modal for a parent item at levelIdx (adds a child at the next level).
  // `childBlock` selects which type to add when the next depth mixes several.
  const openAdd = (parentItem, levelIdx, l1Id, childBlock = null) => {
    const parentLevel = configuredLevels[levelIdx];
    const childLevel = configuredLevels[levelIdx + 1];
    if (!childLevel) return;
    const block = childBlockOf(childLevel, childBlock);
    if (!canAddChildBlock(blockOfItem(parentLevel, parentItem), block)) return;
    if (!canAddBlockType(block)) return;   // no Add permission for this type in its project
    const parentKey = (parentItem.columns && parentItem.columns.Key) || parentItem.name || '';
    const testRun = isTestRunLevel(block);
    setAddCtx({
      levelNum: childLevel.level_num,
      parentKey,
      childType: testRun ? 'Test Case' : (levelTypeLabel(block) || block.name || `L${childLevel.level_num}`),
      childKeyPrefix: testRun ? null : childKeyPrefixFor(childLevel.level_num),
      childTypeCode: testRun ? 'T_CASE' : block.work_item_type,
      childBlockId: block.block_id || null,
      parentLevelNum: parentLevel ? parentLevel.level_num : levelIdx + 1,
      l1Id,
    });
  };

  // Open the same add modal from the Graph view: a node at levelIdx adds a child at
  // the next level. Uses the graph's own levels/rows so it works even if the matrix
  // view hasn't been loaded this session. Reconciles the graph (not the grid) on add.
  const openAddFromGraph = (node, levelIdx, childBlock = null) => {
    const parentLevel = graphLevels[levelIdx];
    const childLevel = graphLevels[levelIdx + 1];
    if (!childLevel) return;
    const block = childBlockOf(childLevel, childBlock);
    if (!canAddChildBlock(blockOfItem(parentLevel, node), block)) return;
    if (!canAddBlockType(block)) return;   // no Add permission for this type in its project
    const parentKey = (node.columns && node.columns.Key) || node.key || node.name || '';
    const testRun = isTestRunLevel(block);
    setAddCtx({
      levelNum: childLevel.level_num,
      parentKey,
      childType: testRun ? 'Test Case' : (levelTypeLabel(block) || block.name || `L${childLevel.level_num}`),
      childKeyPrefix: testRun ? null : childKeyPrefixFor(childLevel.level_num, graphRows),
      childTypeCode: testRun ? 'T_CASE' : block.work_item_type,
      childBlockId: block.block_id || null,
      parentLevelNum: parentLevel ? parentLevel.level_num : levelIdx + 1,
      source: 'graph',
    });
  };

  // Open the add modal to create a brand-new L1 (root) item. L1 has no parent, so this
  // is a create-only flow (no trace) — the modal hides the "traced to" line and AI mode.
  const openAddL1 = () => {
    const l1 = configuredLevels[0];
    if (!l1) return;
    const block = childBlockOf(l1, null);
    if (!canAddBlockType(block)) return;   // no Add permission for the base type in its project
    setAddCtx({
      levelNum: 1,
      parentKey: '',
      childType: levelTypeLabel(block) || block.name || 'L1',
      childKeyPrefix: childKeyPrefixFor(1),
      childTypeCode: block.work_item_type,
      childBlockId: block.block_id || null,
      isRoot: true,
    });
  };

  // AI mode adds items one-by-one; reconcile the grid in the background but keep the
  // modal open so the user can keep adding suggestions.
  const handleAiAdded = async (result) => {
    const fromGraph = addCtx?.source === 'graph';
    loadPendingTraces();
    const key = result.item?.key || 'Item';
    if (result.status !== 'linked' && result.status !== 'created') {
      const why = result.error ? `: ${result.error}` : '';
      showToast(`${key} created, but linking failed${why}`, 'warn');
      return;
    }
    const newId = result.item?.id;
    const sf = result.item?.steps_failed;
    const where = fromGraph ? 'graph' : 'grid';
    showToast(sf ? `Added ${key} (${sf} step(s) failed) — updating ${where}…` : `Added ${key} — updating ${where}…`, 'ok');
    // silent background reconcile of the active view
    const data = await (fromGraph ? fetchGraph(true) : fetchMatrix(true, true));
    if (newId && (data?.rows || []).some((r) => Object.keys(r).some((k) => r[k] && r[k].id === newId))) {
      setRecentlyAddedIds((prev) => new Set([...(prev || []), newId]));
      setTimeout(() => setRecentlyAddedIds(new Set()), 3500);
    }
  };

  const handleAddDone = async (result) => {
    const fromGraph = addCtx?.source === 'graph';
    setAddCtx(null);
    loadPendingTraces();

    if (result.status !== 'linked' && result.status !== 'created') {
      const why = result.error ? `: ${result.error}` : '';
      showToast(`Item ${result.item?.key || ''} created, but linking failed${why}`, 'warn');
      return;
    }

    const newId = result.item?.id;
    const key = result.item?.key || 'Item';
    const where = fromGraph ? 'graph' : 'grid';
    // Reconcile in the background (view stays visible), then confirm the item landed
    showToast(`Created ${key} — updating ${where}…`, 'ok');
    const data = await (fromGraph ? fetchGraph(true) : fetchMatrix(true, true));
    const present = !!newId && (data?.rows || []).some(
      (r) => Object.keys(r).some((k) => r[k] && r[k].id === newId)
    );
    if (result.warning) {
      // Created, but not where it was asked to go (DHF: the paragraph was refused → the pool).
      showToast(`${key}: ${result.warning}`, 'warn');
    } else if (present) {
      setRecentlyAddedIds(new Set([newId]));           // flash it so the eye finds it
      setTimeout(() => setRecentlyAddedIds(new Set()), 3500);
      showToast(`${key} added.`, 'ok');
    } else {
      // Created in Orcanos, but not returned by the panel's filter/trace view
      const how = result.status === 'created' ? 'created' : 'created & linked';
      showToast(`${key} ${how}, but it isn't shown here — it may not match this panel's filter yet.`, 'warn');
    }
  };

  const retryPending = async (id) => {
    try {
      const resp = await fetch(`/api/matrix/pending-traces/${id}/retry`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-CSRF-Token': getCsrfToken() },
      });
      const data = await resp.json();
      if (data.IsSuccess && data.status === 'linked') {
        showToast('Trace linked.', 'ok');
        await loadPendingTraces();
        fetchMatrix(true);
      } else {
        showToast('Still could not link: ' + (data.error || 'unknown error'), 'warn');
      }
    } catch (err) {
      showToast('Error: ' + err.message, 'warn');
    }
  };

  const dismissPending = async (id) => {
    try {
      await fetch(`/api/matrix/pending-traces/${id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'X-CSRF-Token': getCsrfToken() },
      });
      loadPendingTraces();
    } catch (err) {
      console.warn('Dismiss failed:', err);
    }
  };

  // Group rows by L1 ID for hierarchical view
  const groupByL1 = () => {
    const groups = {};
    rows.forEach(row => {
      if (row.l1) {
        const l1Id = row.l1.id;
        if (!groups[l1Id]) {
          groups[l1Id] = { l1: row.l1, children: [] };
        }
        groups[l1Id].children.push(row);
      }
    });
    return Object.values(groups);
  };

  // Columns the panel wizard hid for an item's block ("columns shown" picker). DISPLAY ONLY:
  // item.columns stays whole — Key lookups, add-trace and every engine still read it. A block
  // never loses all of Key / Name / Synopsis; if it would, the ones the item has stay shown.
  const isColumnHidden = (item, levelNum, col) => {
    if (!item || !item.columns) return false;
    const lvl = (configuredLevels || []).find(l => l.level_num === levelNum);
    const types = (lvl && lvl.types) || [];
    const block = types.find(t => t && t.block_id === item.block_id) || (types.length === 1 ? types[0] : null);
    const hidden = (block && block.hidden_columns) || [];
    if (!hidden.includes(col)) return false;
    if (!IDENTITY_COLUMNS.includes(col)) return true;
    return IDENTITY_COLUMNS.some(k => k in item.columns && !hidden.includes(k));
  };

  const getColumnsForLevel = (levelNum) => {
    // A depth can now hold several block types sharing one column lane, so the columns are
    // the UNION of every item's columns at this depth (first-seen order preserved).
    const seen = [];
    for (let row of rows) {
      const item = row[`l${levelNum}`];
      if (item && item.columns) {
        Object.keys(item.columns).forEach(c => {
          // Skip columns with a blank/whitespace name: Orcanos can return a visible
          // field with no Title, which would otherwise render as a headerless "mystery"
          // lane — wide where some row has content, empty elsewhere (the gap before Name).
          if (c == null || String(c).trim() === '') return;
          if (isColumnHidden(item, levelNum, c)) return;
          if (!seen.includes(c)) seen.push(c);
        });
      }
    }
    // A column empty on every row of this page (e.g. an unfilled Root Document Id) is just a
    // blank lane — drop it. Key / Name / Synopsis always stay.
    const hasValue = (c) => rows.some(row => {
      const v = row[`l${levelNum}`]?.columns?.[c];
      return v != null && String(v).trim() !== '' && String(v).trim() !== '-';
    });
    const filled = seen.filter(c => IDENTITY_COLUMNS.includes(c) || hasValue(c));
    let cols = filled.length ? filled : (seen.length ? seen : ['Name']);

    // By default the filter's own column order is honoured as-is. Legacy behaviour
    // (⋮ → "Keep filter column order" off) force-pushes the test-run columns
    // (Result before Version) and Traced Items Info to the end of the level.
    if (!keepFilterOrder) {
      const tail = [];
      ['Last Test Run Result', 'Last Test Run Version', 'Traced Items Info'].forEach(name => {
        const i = cols.indexOf(name);
        if (i !== -1) { cols.splice(i, 1); tail.push(name); }
      });
      cols = [...cols, ...tail];
    }

    // A Test Run shares its Test Case's key, so its "Key" column just repeats the case's
    // key one lane to the left. Drop the whole Key column (header + cells) from a Test Run
    // level that descends from a Test Case — its result columns still show.
    const lvlIdx = (configuredLevels || []).findIndex(l => l.level_num === levelNum);
    const lvl = lvlIdx >= 0 ? configuredLevels[lvlIdx] : null;
    const feeding = lvlIdx > 0 ? configuredLevels[lvlIdx - 1] : null;
    const feedingIsTestCase = !!feeding && !isTestRunLevel(feeding) && (
      String(levelTypeLabel(feeding) || '').toLowerCase().includes('test case') ||
      String(feeding.work_item_type || '').toUpperCase() === 'T_CASE'
    );
    if (lvl && (lvl.is_test_run || isTestRunLevel(lvl)) && feedingIsTestCase) {
      const filtered = cols.filter(col => col !== 'Key');
      if (filtered.length) cols = filtered;
    }

    // Feature 3: Filter out "Traced Items Info" if not showing
    if (!showTracedItems) {
      cols = cols.filter(col => col !== 'Traced Items Info');
    }
    return cols;
  };

  // Fit to screen: share the grid's width by what a column holds. Key columns, type
  // tags and status dots keep their natural width; text columns (Name, Description,
  // Title…) get three shares each, every other column (ids, dates, Root Document Id)
  // one share. Splitting evenly gave an empty Root Document Id as much room as Name,
  // which then broke mid-word. A trace wider than the minimums still side-scrolls.
  const fitKind = (col) => {
    const c = String(col || '').trim();
    if (/^key$/i.test(c)) return 'key';
    return /name|title|synopsis|description|summary|text|statement|requirement/i.test(c) ? 'wide' : 'narrow';
  };
  const fitWidths = () => {
    const lvls = configuredLevels || [];
    let shares = 0;
    let fixed = 34; // status dot column
    lvls.forEach(l => {
      if (isMixedLevel(l)) fixed += 52;
      getColumnsForLevel(l.level_num).forEach(col => {
        const k = fitKind(col);
        fixed += k === 'key' ? 112 : 16; // a key's natural width; else cell padding
        if (k !== 'key') shares += k === 'wide' ? 3 : 1;
      });
    });
    if (!shares || !gridWidth) return { wide: 200, narrow: 90 };
    const unit = Math.max(0, gridWidth - fixed - 12) / shares;
    return {
      wide: Math.floor(Math.max(110, Math.min(360, unit * 3))),
      // 76px fits a one-line status pill ("No Run", "Passed") with the cell padding
      narrow: Math.floor(Math.max(76, Math.min(130, unit))),
    };
  };

  // Stable, well-separated color per work-item type across the whole panel. Types are
  // gathered from every depth and spaced by the golden angle so each reads distinctly.
  const typeColorMap = (() => {
    const codes = [];
    (configuredLevels || []).forEach(l => {
      (l.types || []).forEach(t => {
        const c = String(t.work_item_type || '').toUpperCase();
        if (c && !codes.includes(c)) codes.push(c);
      });
      const lc = String(l.work_item_type || '').toUpperCase();
      if (lc && !codes.includes(lc)) codes.push(lc);
    });
    const map = {};
    codes.forEach((c, i) => {
      const h = Math.round((i * 137.508) % 360);
      map[c] = { background: `hsl(${h} 68% 94%)`, color: `hsl(${h} 60% 30%)`, borderColor: `hsl(${h} 55% 80%)` };
    });
    return map;
  })();

  // A depth with more than one block type gets its own leading "Type" column with a small
  // colored badge per item, so mixed lanes stay legible (e.g. SR vs Risk). The badge never
  // sits inside a filter column — that read as part of the filter's value.
  const isMixedLevel = (level) => !!level && Array.isArray(level.types) && level.types.length > 1;
  const gridColCount = (level) => getColumnsForLevel(level.level_num).length + (isMixedLevel(level) ? 1 : 0);
  const depthTypeBadge = (level, item) => {
    if (!item || !isMixedLevel(level)) return null;
    const code = String(item.type || '').toUpperCase();
    const t = level.types.find(x => String(x.work_item_type || '').toUpperCase() === code);
    const full = t?.type_label || t?.name || item.type;
    return (
      <span className="cell-type-badge" style={typeColorMap[code]} title={full}>{item.type || full}</span>
    );
  };

  const renderCell = (value, columnName = '') => {
    const displayValue = !value ? '' : (typeof value === 'object' ? JSON.stringify(value) : String(value));
    const isHtml = typeof displayValue === 'string' && /<[a-z][\s\S]*>/i.test(displayValue);
    return { value: displayValue, className: '', isHtml };
  };

  // Feature 4: Collapse/Expand handlers
  const toggleL1 = (l1Id) => {
    const newSet = new Set(expandedL1Items);
    if (newSet.has(l1Id)) {
      newSet.delete(l1Id);
    } else {
      newSet.add(l1Id);
    }
    setExpandedL1Items(newSet);
  };

  const expandAll = () => {
    const allIds = new Set(groupByL1().map(g => g.l1.id));
    setExpandedL1Items(allIds);
  };

  const collapseAll = () => {
    setExpandedL1Items(new Set());
  };

  // One button does both: it collapses once every L1 row on the page is open,
  // and expands otherwise (so a partially expanded grid opens the rest first).
  const toggleExpandAll = () => {
    const groups = groupByL1();
    const allOpen = groups.length > 0 && groups.every(g => expandedL1Items.has(g.l1.id));
    if (allOpen) collapseAll(); else expandAll();
  };

  // The "Add <type>" options for a child of `item` (at levelIdx) — the same rules as the
  // hover ＋, shared by the gap slot and the chain panel. [] when nothing can be added.
  const addChildOptions = (item, levelIdx) => {
    if (!canAdd || !item || levelIdx < 0 || levelIdx >= configuredLevels.length - 1) return [];
    const nextLevel = configuredLevels[levelIdx + 1];
    let blocks = (Array.isArray(nextLevel.types) && nextLevel.types.length)
      ? nextLevel.types
      : [null];
    // Branching tree: several blocks can share the next depth's column
    // lane while descending from DIFFERENT parents. Only offer ＋ for the
    // child blocks that actually descend from THIS item's block, so an
    // item isn't shown a sibling branch's add (e.g. two "＋ Test Case").
    if (item.block_id && blocks.some((b) => b && b.parent_id)) {
      const owned = blocks.filter((b) => b && b.parent_id === item.block_id);
      if (owned.length) blocks = owned;
    }
    // A Test Run under a Test Case can't be authored (it comes from
    // executing the case) — offer no ＋ for that hop.
    const parentBlock = blockOfItem(configuredLevels[levelIdx], item);
    blocks = blocks.filter((blk) => canAddChildBlock(parentBlock, blk || nextLevel));
    // Add permission is per type per project: drop any type the user
    // can't add (so the ＋ / its menu only offers authorable types).
    blocks = blocks.filter((blk) => canAddBlockType(childBlockOf(nextLevel, blk)));
    // Safety net: collapse any remaining same-label options (e.g. a Test
    // Case + a Test Run block that both add a "Test Case").
    const seenLabels = new Set();
    return blocks.filter((blk) => {
      const label = isTestRunLevel(blk || nextLevel) ? 'Test Case' : levelTypeLabel(blk || nextLevel);
      if (seenLabels.has(label)) return false;
      seenLabels.add(label);
      return true;
    }).map((blk) => ({
      blk,
      label: isTestRunLevel(blk || nextLevel) ? 'Test Case' : levelTypeLabel(blk || nextLevel),
    }));
  };

  const addTitle = (item, options) => {
    const parentKeyLabel = item.columns?.Key || item.name || '';
    return options.length === 1
      ? `Add ${options[0].label} traced to ${parentKeyLabel}`
      : `Add an item traced to ${parentKeyLabel}`;
  };

  // One type → straight to the add modal; several → the small "Add <type>" menu.
  const startAdd = (e, item, levelIdx, l1Id, options) => {
    e.stopPropagation();
    if (options.length === 1) { openAdd(item, levelIdx, l1Id, options[0].blk); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    setAddMenu({
      x: rect.left,
      y: rect.bottom,
      options,
      onPick: (blk) => { setAddMenu(null); openAdd(item, levelIdx, l1Id, blk); },
    });
  };

  // A Test Run level (by type, or flagged by the backend) — folded into its test in the chain panel.
  const isRunLevelAt = (i) => {
    const lvl = configuredLevels[i];
    return !!lvl && (lvl.is_test_run || isTestRunLevel(lvl));
  };

  // The chain panel's tree for one requirement: its rows merged by item id per level, so
  // one item traced to several children shows once with all of them under it. A row that
  // stops early adds a "missing" (gap) or "not executed" leaf where it stops.
  const buildChain = (group) => {
    const root = { kids: new Map() };
    group.children.forEach((row) => {
      const miss = missingColumnForRow(row, configuredLevels);
      const notExec = notExecutedColumnForRow(row, configuredLevels);
      let node = root;
      for (let i = 1; i < configuredLevels.length; i += 1) {
        const item = row[`l${configuredLevels[i].level_num}`];
        // A Test Run is not a level of its own here: its result rides on the test above it
        // as a pill ("Not executed" when the test has no run yet).
        if (node.kind === 'item' && isRunLevelAt(i)) {
          if (item) {
            const r = runResultOf(item) || 'Executed';
            if (!node.runs.includes(r)) node.runs.push(r);
          } else if (i === notExec) {
            node.notExec = true;
          }
          break;
        }
        if (!item) {
          const kind = i === miss ? 'gap' : (i === notExec ? 'notexec' : null);
          if (kind && !node.kids.has(`${kind}:${i}`)) {
            const parentItem = row[`l${configuredLevels[i - 1].level_num}`];
            node.kids.set(`${kind}:${i}`, { kind, levelIdx: i, parentItem, kids: new Map() });
          }
          break;
        }
        const id = `${i}:${item.id}`;
        if (!node.kids.has(id)) node.kids.set(id, { kind: 'item', levelIdx: i, item, runs: [], notExec: false, kids: new Map() });
        node = node.kids.get(id);
      }
    });
    // Each node keeps its path and its ancestors' paths, so a collapsed branch hides its subtree.
    const flat = [];
    const walk = (n, ancestors) => n.kids.forEach((k, key) => {
      k.path = `${ancestors.length ? ancestors[ancestors.length - 1] : ''}/${key}`;
      k.ancestors = ancestors;
      k.hasKids = k.kids.size > 0;
      flat.push(k);
      walk(k, [...ancestors, k.path]);
    });
    walk(root, []);
    return flat;
  };

  const openChain = (l1Id) => { setChainL1(l1Id); setShowChain(true); };

  // The chain panel is as wide as the user drags it: 260 px up to 70% of the window.
  const clampChainWidth = (w) => Math.round(Math.max(260, Math.min(w, window.innerWidth * 0.7)));
  const startChainResize = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = chainWidth;
    const onMove = (ev) => setChainWidth(clampChainWidth(startW + (startX - ev.clientX)));
    const onUp = () => {
      window.getSelection()?.removeAllRanges();   // a drag must not leave grid text selected
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      document.body.classList.remove('chain-resizing');
    };
    document.body.classList.add('chain-resizing');
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };
  const onChainResizeKey = (e) => {
    if (e.key === 'ArrowLeft') { e.preventDefault(); setChainWidth((w) => clampChainWidth(w + 24)); }
    if (e.key === 'ArrowRight') { e.preventDefault(); setChainWidth((w) => clampChainWidth(w - 24)); }
  };

  const openExportModal = () => {
    setShowExportModal(true);
  };

  if (loading) return <div className="loading">Loading matrix...</div>;

  // Status per requirement on this page; the filter bar narrows the grid to one status.
  // The server pages L1 items, so the counts and the filter cover THIS page only.
  const pageGroups = groupByL1();
  const statusOf = {};
  const statusCounts = { all: pageGroups.length, gap: 0, notexec: 0, ok: 0 };
  pageGroups.forEach((g) => {
    const s = groupStatus(g, configuredLevels);
    statusOf[g.l1.id] = s;
    statusCounts[s] += 1;
  });
  const l1Groups = statusFilter === 'all'
    ? pageGroups
    : pageGroups.filter((g) => statusOf[g.l1.id] === statusFilter);
  const chainGroup = showChain
    ? (pageGroups.find((g) => g.l1.id === chainL1) || l1Groups[0] || null)
    : null;

  // Header coverage for level idx: of this level's items (whole panel), how many have a child at the
  // NEXT level — L1 covered by L2, L2 by L3 … The last level has none (Note #120).
  const coverageTone = (pct) => (pct >= 80 ? 'good' : pct >= 50 ? 'mid' : 'low');
  const renderLevelCoverage = (idx) => {
    const lv = coverage?.down?.[idx];
    if (!lv || !lv.total) return null;
    const pct = Math.round((100 * lv.n) / lv.total);
    return (
      <span className="lvl-cov" title={`${lv.n} of ${lv.total} ${lv.name} items are covered by ${lv.next} (whole panel, built ${formatBuiltAt(coverage.built_at)})`}>
        <span className="lvl-cov-bar"><i className={`lvl-cov-fill lvl-cov-fill--${coverageTone(pct)}`} style={{ width: `${pct}%` }} /></span>
        <b className="lvl-cov-pct">{pct}%</b>
      </span>
    );
  };

  const itemTitle = (item) => item?.name || item?.columns?.Name || item?.columns?.Synopsis || '';
  const runResultOf = (item) => {
    const col = Object.keys(item?.columns || {}).find((c) => RUN_RESULT_COL.test(c));
    const v = col ? item.columns[col] : '';
    return v && v !== '-' ? String(v) : '';
  };

  // The right-hand chain panel: the selected requirement's whole trace as a tree.
  const renderChainPanel = () => {
    if (!chainGroup) {
      return (
        <aside className="chain-panel" aria-label="Chain">
          <div className="chain-head">
            <span className="chain-eyebrow">Chain</span>
            <button type="button" className="chain-close" onClick={() => setShowChain(false)} aria-label="Hide the chain panel">×</button>
          </div>
          <p className="chain-empty">Nothing on this page to show.</p>
        </aside>
      );
    }
    const l1 = chainGroup.l1;
    const status = statusOf[l1.id];
    const nodes = buildChain(chainGroup);
    const perLevel = configuredLevels.slice(1).map((lvl, i) => ({
      name: lvl.name,
      count: nodes.filter((n) => n.kind === 'item' && n.levelIdx === i + 1).length,
    })).filter((x) => x.count > 0);
    const notRun = nodes.filter((n) => n.kind === 'item' && n.notExec && !n.runs.length).length;
    const foldKey = (path) => `${l1.id}${path}`;
    const isFolded = (n) => chainCollapsed.has(foldKey(n.path));
    const toggleFold = (n) => setChainCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(foldKey(n.path))) next.delete(foldKey(n.path)); else next.add(foldKey(n.path));
      return next;
    });
    const parents = nodes.filter((n) => n.hasKids);
    const anyFolded = parents.some(isFolded);
    // Collapse all folds the top branches; Expand all unfolds every branch of this item.
    const foldAll = () => setChainCollapsed((prev) => {
      const next = new Set([...prev].filter((k) => !k.startsWith(`${l1.id}/`)));
      if (!anyFolded) parents.filter((n) => !n.ancestors.length).forEach((n) => next.add(foldKey(n.path)));
      return next;
    });
    const visible = nodes.filter((n) => !n.ancestors.some((a) => chainCollapsed.has(foldKey(a))));
    const gaps = nodes.filter((n) => n.kind === 'gap').length;
    return (
      <aside className="chain-panel" aria-label={`Chain of ${itemKeyOf(l1)}`}>
        <div className="chain-head">
          <span className="chain-eyebrow">Selected chain</span>
          <span className={`chain-status chain-status--${status}`}>{GROUP_STATUS_LABEL[status]}</span>
          {parents.length > 0 && (
            <button
              type="button"
              className="chain-fold-all"
              onClick={foldAll}
              title={anyFolded ? 'Expand every branch' : 'Collapse to the first level'}
              aria-label={anyFolded ? 'Expand all' : 'Collapse all'}
            >
              {anyFolded ? <UnfoldMoreIcon /> : <UnfoldLessIcon />}
            </button>
          )}
          <button type="button" className="chain-close" onClick={() => setShowChain(false)} aria-label="Hide the chain panel" title="Hide the chain panel">×</button>
        </div>
        <div className="chain-root">
          <span className="chain-level">{configuredLevels[0]?.name}</span>
          {l1.url
            ? <a className="chain-key" href={l1.url} target="_blank" rel="noopener noreferrer">{itemKeyOf(l1)}</a>
            : <span className="chain-key">{itemKeyOf(l1)}</span>}
          <p className="chain-title">{itemTitle(l1)}</p>
          <p className="chain-summary">
            {perLevel.length
              ? perLevel.map((x) => `${x.count} ${x.name}`).join(' · ')
              : 'Nothing traced below it yet'}
            {gaps > 0 && ` · ${gaps} gap${gaps === 1 ? '' : 's'}`}
            {notRun > 0 && ` · ${notRun} not executed`}
          </p>
        </div>
        <ul className="chain-tree">
          {visible.map((n) => {
            const level = configuredLevels[n.levelIdx];
            const indent = { marginLeft: `${(n.levelIdx - 1) * 16}px` };
            if (n.kind === 'item') {
              // The test's own result column, else its folded run(s), else "Not executed".
              const own = runResultOf(n.item);
              const results = own ? [own] : (n.runs.length ? n.runs : (n.notExec ? ['Not executed'] : []));
              return (
                <li key={n.path} className="chain-node" style={indent}>
                  {n.hasKids ? (
                    <button
                      type="button"
                      className={`chain-fold${isFolded(n) ? ' is-folded' : ''}`}
                      onClick={() => toggleFold(n)}
                      aria-expanded={!isFolded(n)}
                      aria-label={`${isFolded(n) ? 'Expand' : 'Collapse'} ${itemKeyOf(n.item)}`}
                    >
                      <ChevronIcon />
                    </button>
                  ) : <span className="chain-fold-spacer" aria-hidden="true" />}
                  <span className="chain-dot" aria-hidden="true" />
                  <div className="chain-node-body">
                    <span className="chain-level">{level?.name}</span>
                    <span className="chain-node-key">
                      {n.item.url
                        ? <a className="chain-key" href={n.item.url} target="_blank" rel="noopener noreferrer">{itemKeyOf(n.item)}</a>
                        : <span className="chain-key">{itemKeyOf(n.item)}</span>}
                      {results.map((r) => <span key={r} className={`status-pill ${testRunPillClass(r)}`}>{r}</span>)}
                    </span>
                    <span className="chain-node-title">{itemTitle(n.item)}</span>
                  </div>
                </li>
              );
            }
            if (n.kind === 'notexec') {
              return (
                <li key={n.path} className="chain-node chain-node--notexec" style={indent}>
                  <span className="chain-fold-spacer" aria-hidden="true" />
                  <span className="chain-dot" aria-hidden="true" />
                  <div className="chain-node-body">
                    <span className="chain-level">{level?.name}</span>
                    <span className="chain-node-title">Not executed yet</span>
                  </div>
                </li>
              );
            }
            const options = addChildOptions(n.parentItem, n.levelIdx - 1);
            return (
              <li key={n.path} className="chain-node chain-node--gap" style={indent}>
                <span className="chain-fold-spacer" aria-hidden="true" />
                <span className="chain-dot" aria-hidden="true" />
                <div className="chain-node-body">
                  <span className="chain-level">{level?.name}</span>
                  <span className="chain-node-title">Missing — nothing traced to {itemKeyOf(n.parentItem)}</span>
                  {options.length > 0 && (
                    <button
                      type="button"
                      className="chain-add"
                      aria-haspopup={options.length === 1 ? undefined : 'menu'}
                      onClick={(e) => startAdd(e, n.parentItem, n.levelIdx - 1, l1.id, options)}
                    >
                      <PlusIcon /> {options.length === 1 ? `Add ${options[0].label}` : 'Add…'}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
        <p className="chain-hint">Click any row in the grid to show its chain here.</p>
      </aside>
    );
  };
  const allRowsExpanded = l1Groups.length > 0 && l1Groups.every(g => expandedL1Items.has(g.l1.id));

  // Apply the typed L1 search: reset to page 1 and set the applied term (the
  // fetch effect re-runs on `search`). Clearing restores the full matrix.
  const applySearch = () => {
    const term = searchInput.trim();
    if (term === search) return;
    setPage(1);
    setGraphPage(1);
    setSearch(term);
  };
  const clearSearch = () => {
    setSearchInput('');
    if (search) { setPage(1); setGraphPage(1); setSearch(''); }
  };

  return (
    <div className="matrix-page">
      {maximized && (
        <>
          {/* Edge hover zones — nearing the top/bottom reveals the bar overlay (CSS ~ selectors) */}
          <div className="max-hz max-hz-top" aria-hidden="true" />
          <div className="max-hz max-hz-bottom" aria-hidden="true" />
          {/* Always-visible escape hatch so hover-only reveal can never trap the user */}
          <button
            type="button"
            className="max-exit-pill"
            onClick={() => setMaximized(false)}
            title="Exit maximize (Esc)"
            aria-label="Exit maximize"
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="M6 2v4H2M10 2v4h4M6 14v-4H2M10 14v-4h4" /></svg>
            <span>Exit</span>
          </button>
        </>
      )}
      <div className="matrix-toolbar">
        <div className="matrix-nav-group">
          <button onClick={() => navigate(backTo)} className="btn-nav" title="Back">← Back</button>
        </div>

        <span className="toolbar-divider" aria-hidden="true" />

        <h1 className="panel-title" title={panelName || `Panel ${setupId}`}>
          <span className="panel-title-name">{panelName || `#${setupId}`}</span>
        </h1>

        {canManagePanels && (
          <button onClick={() => navigate(`/trace/config/${setupId}`, { state: { from: `/trace/matrix/${setupId}` } })} className="btn-nav btn-edit-panel" title="Edit this panel's settings" aria-label="Edit this panel's settings">✎</button>
        )}

        <span className="toolbar-divider" aria-hidden="true" />

        <ViewSwitcher
          views={VIEW_MODES}
          value={viewMode}
          onChange={setViewMode}
          ariaLabel="View mode"
          className={viewMode === 'funnel' || viewMode === 'dashboard' ? 'vsw--solo' : ''}
        />

        {(viewMode === 'matrix' || viewMode === 'graph') && (
          <div className="l1-search" title="Search level-1 items by name, description, or ID">
            <button type="button" className="l1-search-icon-btn" onClick={applySearch} title="Search" aria-label="Search level-1 items">
              <svg className="l1-search-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><circle cx="7" cy="7" r="4.5" /><path d="M10.5 10.5L14 14" /></svg>
            </button>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') applySearch(); if (e.key === 'Escape') clearSearch(); }}
              placeholder="Search L1 (name, description, ID)…"
              aria-label="Search level-1 items"
            />
            {(searchInput || search) && (
              <button type="button" className="l1-search-clear" onClick={clearSearch} title="Clear search" aria-label="Clear search">×</button>
            )}
          </div>
        )}
        {viewMode === 'matrix' && (
          <div className="matrix-view-toggles">
            <button
              type="button"
              className={`matrix-icon-btn${showFilterBar ? ' is-on' : ''}`}
              onClick={() => setShowFilterBar((v) => !v)}
              aria-pressed={showFilterBar}
              aria-label="Filters and columns"
              title={showFilterBar ? 'Hide filters and columns' : 'Show filters and columns'}
            >
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" aria-hidden="true"><path d="M2 3h12L9.5 8.6V13l-3-1.5V8.6z" /></svg>
              {statusFilter !== 'all' && <span className="matrix-icon-badge" aria-hidden="true">1</span>}
            </button>
            <button
              type="button"
              className={`matrix-icon-btn${showChain ? ' is-on' : ''}`}
              onClick={() => setShowChain((v) => !v)}
              aria-pressed={showChain}
              aria-label="Chain panel"
              title={showChain ? 'Hide the chain panel' : 'Show the chain panel — the full trace of the selected row'}
            >
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><rect x="1.5" y="2.5" width="13" height="11" rx="1.5" /><path d="M10 2.5v11" /></svg>
            </button>
          </div>
        )}

        <button
          type="button"
          className="btn-maximize"
          onClick={() => setMaximized(m => !m)}
          title={maximized ? 'Exit maximize (Esc)' : 'Maximize the working area'}
          aria-label={maximized ? 'Exit maximize' : 'Maximize the working area'}
          aria-pressed={maximized}
        >
          {maximized ? (
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="M6 2v4H2M10 2v4h4M6 14v-4H2M10 14v-4h4" /></svg>
          ) : (
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="M2 6V2h4M14 6V2h-4M2 10v4h4M14 10v4h-4" /></svg>
          )}
        </button>

        {viewMode === 'matrix' && (
          <button
            type="button"
            className={`btn-maximize btn-fit${fitScreen ? ' is-on' : ''}`}
            onClick={() => setFitScreen(f => !f)}
            title={fitScreen ? 'Back to regular size' : 'Fit the whole trace on one screen'}
            aria-label={fitScreen ? 'Back to regular size' : 'Fit the whole trace on one screen'}
            aria-pressed={fitScreen}
          >
            {fitScreen ? (
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M1.5 3v10M14.5 3v10M4 8h8M6 6L4 8l2 2M10 6l2 2-2 2" /></svg>
            ) : (
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M1.5 3v10M14.5 3v10M3.5 8h3M9.5 8h3M5 6l1.5 2L5 10M11 6L9.5 8l1.5 2" /></svg>
            )}
          </button>
        )}
        {(viewMode === 'matrix' || viewMode === 'graph') && (
        <div className="toolbar-actions">
          {canAskPaul && canAdd && (
            <button type="button" className="btn-build-ai" onClick={() => navigate(`/trace/build/${setupId}`)}
              title="Open the AI Trace Builder — generate and link items level by level">✨ AI Studio</button>
          )}
          <button type="button" className="btn-export-toolbar" onClick={openExportModal} title="Export the matrix">⬇ Export…</button>
          <button
            type="button"
            className="toolbar-refresh"
            onClick={() => (viewMode === 'graph' ? fetchGraph(true) : fetchMatrix(true))}
            disabled={viewMode === 'graph' && graphLoading}
            title={
              viewMode === 'graph'
                ? (graphCachedAt ? `Cached ${formatBuiltAt(graphCachedAt)} — click to refresh from Orcanos` : 'Refresh graph data from Orcanos')
                : (matrixCachedAt ? `Cached ${formatBuiltAt(matrixCachedAt)} — click to refresh from Orcanos` : 'Refresh matrix data from Orcanos')
            }
            aria-label="Refresh from Orcanos"
          >
            <span className={viewMode === 'graph' && graphLoading ? 'refresh-spin' : undefined}>⟳</span>
          </button>
        </div>
        )}
        {backgroundExportProgress !== null && (
          <div style={{position: 'relative', display: 'flex', alignItems: 'center'}}>
            <button
              onClick={() => setShowExportCancelMenu(!showExportCancelMenu)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 12px',
                fontSize: '13px',
                fontWeight: '600',
                color: '#2196f3',
                background: '#e3f2fd',
                border: '1px solid #90caf9',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = '#bbdefb'}
              onMouseLeave={(e) => e.target.style.background = '#e3f2fd'}
              title="Click to cancel export"
            >
              <span>⬇</span>
              <span>Exporting {backgroundExportProgress}%</span>
            </button>
            {showExportCancelMenu && currentExportJobId && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: '0',
                marginTop: '4px',
                background: 'white',
                border: '1px solid #ddd',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                zIndex: 1000,
                minWidth: '200px'
              }}>
                <button
                  onClick={async () => {
                    try {
                      const response = await fetch(`/api/matrix/export-jobs/${currentExportJobId}`, {
                        method: 'DELETE',
                        credentials: 'include',
                        headers: {
                          'X-CSRF-Token': getCsrfToken()
                        }
                      });
                      if (response.ok) {
                        setShowExportCancelMenu(false);
                        setBackgroundExportProgress(null);
                        setCurrentExportJobId(null);
                        alert('Export cancelled');
                      } else {
                        alert('Failed to cancel: ' + response.status);
                      }
                    } catch (err) {
                      alert('Error: ' + err.message);
                    }
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '12px',
                    background: 'none',
                    border: 'none',
                    textAlign: 'left',
                    color: '#f44336',
                    cursor: 'pointer',
                    fontSize: '14px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#ffebee'}
                  onMouseLeave={(e) => e.target.style.background = 'none'}
                >
                  Cancel Export
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {viewMode === 'matrix' && showFilterBar && (
        <div className="matrix-filter-bar" role="toolbar" aria-label="Filters and columns">
          <span className="matrix-filter-label">Show</span>
          {STATUS_FILTERS.filter((f) => f.id !== 'notexec' || statusCounts.notexec > 0).map((f) => (
            <button
              key={f.id}
              type="button"
              className={`matrix-filter-pill matrix-filter-pill--${f.id}${statusFilter === f.id ? ' is-on' : ''}`}
              aria-pressed={statusFilter === f.id}
              onClick={() => setStatusFilter(f.id)}
            >
              <span className="matrix-filter-dot" aria-hidden="true" />
              {f.label}
              <span className="matrix-filter-count">{statusCounts[f.id]}</span>
            </button>
          ))}
          <span className="matrix-filter-note">on this page</span>
          <span className="matrix-filter-spacer" />
          {matrixCachedAt && <span className="matrix-filter-note">Built {formatBuiltAt(matrixCachedAt)}</span>}
          <div className="matrix-columns-wrap">
            <button
              type="button"
              className="matrix-columns-btn"
              aria-haspopup="true"
              aria-expanded={showColumnsMenu}
              onClick={() => setShowColumnsMenu((v) => !v)}
            >
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true"><path d="M2.5 4h11M4.5 8h7M6.5 12h3" /></svg>
              Columns
            </button>
            {showColumnsMenu && (
              <>
                {/* click-anywhere backdrop: any click outside the menu closes it */}
                <div className="matrix-columns-backdrop" onClick={() => setShowColumnsMenu(false)} />
                <div className="matrix-columns-menu">
                  <label>
                    <input type="checkbox" checked={showTracedItems} onChange={(e) => setShowTracedItems(e.target.checked)} />
                    Show Traced Items Info
                  </label>
                  <label title="Wrap long text inside the column instead of trimming it to one line">
                    <input type="checkbox" checked={wrapText} onChange={(e) => setWrapText(e.target.checked)} />
                    Wrap text
                  </label>
                  <label title="Show the columns in the order the Orcanos filter defines them. Off: test-run and Traced Items columns are pushed to the end of each level.">
                    <input type="checkbox" checked={keepFilterOrder} onChange={(e) => setKeepFilterOrder(e.target.checked)} />
                    Keep filter column order
                  </label>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {viewMode === 'funnel' && (
        <div className="funnel-snapbar">
          <div className="funnel-snap-info">
            {funnel && funnel.cached ? (
              <>
                <span className="funnel-dot" />
                <span>Showing cached results · built <strong>{formatBuiltAt(funnel.built_at)}</strong></span>
                {/* A smart refresh that quietly escalated to a full read must say so — a
                    four-minute "refresh" that reports nothing teaches people to distrust
                    the faster button and reach for the slow one every time. */}
                {funnel.funnel?.refresh && (
                  <span
                    className="funnel-refresh-note"
                    title={funnel.funnel.refresh.escalated
                      ? `Read in full: ${funnel.funnel.refresh.reason}`
                      : `${funnel.funnel.refresh.read || 0} row(s) re-read, ${funnel.funnel.refresh.held || 0} held from the last build`}
                  >
                    {funnel.funnel.refresh.escalated
                      ? '· read in full'
                      : `· ${funnel.funnel.refresh.changed || 0} changed`}
                  </span>
                )}
              </>
            ) : (
              <>
                <span className="funnel-dot idle" />
                <span>No snapshot yet — build the coverage funnel from the full matrix.</span>
              </>
            )}
          </div>
          {funnelProgress === null ? (
            <span className="funnel-btn-row">
              {/* Only once a snapshot exists: with nothing stored to merge onto, a smart
                  refresh would immediately escalate to a full read, so offering it would be
                  offering the same button twice under two different names. */}
              {funnel && funnel.cached && (
                <button
                  className="funnel-refresh-btn funnel-refresh-smart"
                  onClick={() => refreshFunnel('smart')}
                  title="Re-read only what changed in Orcanos since the last build"
                >
                  ⤾ Smart refresh
                </button>
              )}
              <button className="funnel-refresh-btn" onClick={refreshFunnel}>
                ↻ {funnel && funnel.cached ? 'Refresh' : 'Build funnel'}
              </button>
            </span>
          ) : (
            <div className="funnel-progress">
              <span className="funnel-build-note"><span className="funnel-spin">↻</span> Rebuilding — cached results stay live</span>
              <div className="funnel-pbar"><div className="funnel-pfill" style={{ width: `${funnelProgress}%` }} /></div>
              <span className="funnel-plabel">{funnelMessage || 'Building...'} {funnelProgress}%</span>
            </div>
          )}
        </div>
      )}

      {error && viewMode === 'matrix' && <div className="error-message">{error}</div>}
      {funnelError && viewMode === 'funnel' && <div className="error-message">{funnelError}</div>}

      {viewMode === 'matrix' && pendingTraces.length > 0 && (
        <div className="pending-strip">
          <span className="pending-strip-title">
            <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" width="15" height="15">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {pendingTraces.length} item{pendingTraces.length > 1 ? 's' : ''} created but not linked
          </span>
          <div className="pending-chips">
            {pendingTraces.map((p) => (
              <span className="pending-chip" key={p.id} title={p.error || 'Trace link failed'}>
                <b>{p.source_key}</b> → {p.target_key}
                <button className="pending-retry" onClick={() => retryPending(p.id)}>Retry</button>
                <button className="pending-dismiss" onClick={() => dismissPending(p.id)} title="Dismiss">×</button>
              </span>
            ))}
          </div>
        </div>
      )}

      {viewMode === 'matrix' && search && (
        <div className="l1-search-banner">
          <span>
            Showing L1 items matching <strong>“{search}”</strong>
            {!loading && ` · ${l1Groups.length} result${l1Groups.length === 1 ? '' : 's'}`}
          </span>
          <button type="button" onClick={clearSearch}>Clear search</button>
        </div>
      )}

      {viewMode === 'matrix' && !loading && search && l1Groups.length === 0 ? (
        <div className="l1-search-empty">
          No level-1 items match <strong>“{search}”</strong>.
          <div>Try a different name, description keyword, or ID.</div>
        </div>
      ) : viewMode === 'matrix' && (
      <div className={`matrix-body${chainGroup ? ' has-chain' : ''}`}>
      <div className="matrix-container" ref={gridContainerRef}>
        <table
          className={`matrix-table ${wrapText || fitScreen ? 'wrap-on' : 'wrap-off'}${fitScreen ? ' fit-on' : ''}`}
          style={fitScreen ? (() => {
            const w = fitWidths();
            return { '--matrix-cell-max': `${w.wide}px`, '--fit-wide': `${w.wide}px`, '--fit-narrow': `${w.narrow}px` };
          })() : undefined}
        >
          <thead>
            {/* Level-name row doubles as the actions bar: the matrix actions
                (Add / Expand / Collapse) live in the L1 group header, absolutely
                positioned so their width can't inflate the L1 column lane (the
                gap-before-Name regression V2.8.2 fixed) — saving a whole row. */}
            <tr ref={groupHeadRef}>
              <th className="group-head-cell status-col" style={{ top: 0 }}><span className="sr-only">Status</span></th>
              {configuredLevels.map((level, idx) => {
                const columns = getColumnsForLevel(level.level_num);
                // ⓘ — hover shows this level's source config (project + version, work-item
                // type, filter id). On L1 it rides with the other right-side actions.
                const infoBtn = (
                  <button
                    type="button"
                    className="head-info-btn"
                    aria-label={`${level.name} level settings`}
                    onMouseEnter={(e) => showLevelCard(e, level)}
                    onMouseLeave={hideLevelCard}
                    onFocus={(e) => showLevelCard(e, level)}
                    onBlur={hideLevelCard}
                  >
                    <InfoIcon />
                  </button>
                );
                return (
                  <th
                    key={idx}
                    ref={idx === 0 ? l1HeadRef : undefined}
                    colSpan={columns.length + (isMixedLevel(level) ? 1 : 0)}
                    className={`group-head-cell${idx > 0 ? ' lvl-start' : ''}${idx === 0 ? ' group-head-l1' : ''}`}
                    style={{ top: 0 }}
                  >
                    {idx === 0 ? (
                      <>
                        <span className="group-head-label" ref={l1LabelRef}>{level.name}</span>
                        {renderLevelCoverage(0)}
                        <div className={`matrix-actions-strip${compactAdd ? ' is-compact' : ''}`} ref={actionsRef}>
                          {configuredLevels[0] && canAddBlockType(childBlockOf(configuredLevels[0], null)) && (
                            <button
                              onClick={openAddL1}
                              className="btn-primary btn-add-l1"
                              title={`Add a new ${levelTypeLabel(configuredLevels[0]) || 'L1'} item`}
                            >
                              <PlusIcon /><span className="btn-add-l1-type" ref={addTypeRef}>Add</span>
                            </button>
                          )}
                          {/* One icon-only toggle (tooltip-labelled): expand all, or collapse
                              all once every row is open. Keeps the strip narrow so it can't
                              ride over the level name in a tight L1 lane. */}
                          <button
                            onClick={toggleExpandAll}
                            className="expand-toggle-btn"
                            title={allRowsExpanded ? 'Collapse all rows' : 'Expand all rows'}
                            aria-label={allRowsExpanded ? 'Collapse all rows' : 'Expand all rows'}
                            aria-pressed={allRowsExpanded}
                          >
                            {allRowsExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
                          </button>
                          {infoBtn}
                        </div>
                      </>
                    ) : (
                      <>
                        <span className="group-head-inner">
                          <span className="group-head-label">{level.name}</span>
                          {renderLevelCoverage(idx)}
                          {infoBtn}
                        </span>
                      </>
                    )}
                  </th>
                );
              })}
            </tr>
            <tr>
              <th className="col-head-cell status-col" style={{ top: colHeadTop }} aria-hidden="true" />
              {configuredLevels.map((level, levelIdx) => {
                const columns = getColumnsForLevel(level.level_num);
                const mixed = isMixedLevel(level);
                return (
                  <React.Fragment key={levelIdx}>
                    {mixed && (
                      <th className={`col-head-cell mx-type-col${levelIdx > 0 ? ' lvl-start' : ''}`} style={{ top: colHeadTop }}>Type</th>
                    )}
                    {columns.map((col, colIdx) => (
                      <th key={`${levelIdx}-${colIdx}`} className={`col-head-cell${colIdx === 0 && levelIdx > 0 && !mixed ? ' lvl-start' : ''} fit-${fitKind(col)}`} style={{ top: colHeadTop }}>
                        {col}
                      </th>
                    ))}
                  </React.Fragment>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {l1Groups.map((group, groupIdx) => {
              const l1Item = group.l1;
              const l1Id = l1Item.id;
              const isExpanded = expandedL1Items.has(l1Id);
              const hasDetailRows = group.children.length > 1 ||
                configuredLevels.length > 1 && group.children.some(r =>
                  configuredLevels.slice(1).some(l => r[`l${l.level_num}`])
                );

              // Track the previously PRINTED row's item per level, to deduplicate
              // a repeated item down the page. Keyed by level key, committed only
              // for rows that actually render.
              const prevLevels = {};

              return (
                <React.Fragment key={groupIdx}>
                  {group.children.map((row, rowInGroupIdx) => {
                    // Add divider after each L1 group (except the last one)
                    // L1: show only on first row of group (already grouped by L1)
                    const showL1 = rowInGroupIdx === 0;

                    // Deepest present level in the ORIGINAL row → the next level
                    // is a genuine missing trace (matches the dashboard). A Test Run
                    // that hasn't run yet is a separate, non-gap "Not Executed" break.
                    const missingAtIndex = missingColumnForRow(row, configuredLevels);
                    const notExecAtIndex = notExecutedColumnForRow(row, configuredLevels);
                    const breakIndex = missingAtIndex !== -1 ? missingAtIndex : notExecAtIndex;
                    const breakKind = missingAtIndex !== -1 ? 'missing' : (notExecAtIndex !== -1 ? 'notexec' : null);

                    // Which levels this row prints. A level prints when its item
                    // differs from the row above OR when a SHALLOWER level already
                    // printed on this row: a new parent starts a new chain, so a
                    // child shared by two parents (one test case covering two
                    // requirements) must be repeated under each of them — even when
                    // the two rows are adjacent and the child id never changed.
                    const levelVisible = {};
                    let ancestorPrinted = false;
                    configuredLevels.forEach((level, levelIdx) => {
                      const key = `l${level.level_num}`;
                      const currentId = row[key]?.id ?? null;
                      if (levelIdx === 0) {
                        levelVisible[key] = !!(showL1 && row.l1);
                      } else {
                        levelVisible[key] = !!currentId &&
                          (ancestorPrinted || currentId !== (prevLevels[key] ?? null));
                      }
                      if (levelVisible[key]) ancestorPrinted = true;
                    });

                    // Feature 4: Check if L1 is collapsed and this is not the first row
                    if (!isExpanded && !showL1) {
                      return null;
                    }

                    // Skip empty rows (nothing new at any level)
                    const hasContent = configuredLevels.some(l => levelVisible[`l${l.level_num}`]);
                    if (!hasContent) return null;

                    // Commit the dedup state only for rows that actually render, so a
                    // skipped row can't shift what the next one compares against.
                    configuredLevels.forEach((level) => {
                      const key = `l${level.level_num}`;
                      prevLevels[key] = row[key]?.id ?? null;
                    });

                    // Everything this row prints, so a shared row can be found again and a
                    // right-click knows what it is pointing at.
                    const printedKeys = configuredLevels
                      .filter((l) => levelVisible[`l${l.level_num}`])
                      .map((l) => itemKeyOf(row[`l${l.level_num}`]))
                      .filter(Boolean);
                    const isFocusRow = !!focusKey && printedKeys.includes(focusKey);

                    return (
                      <tr
                        key={`${groupIdx}-${rowInGroupIdx}`}
                        data-req={l1Id}
                        ref={isFocusRow ? focusRowRef : undefined}
                        className={`${showL1 ? 'l1-row' : 'detail-row'}${isFocusRow ? ' collab-focus-row' : ''}${chainGroup && chainGroup.l1.id === l1Id ? ' chain-selected' : ''}`}
                        onClick={() => {
                          // Chain panel open: a click anywhere on the row shows it there (the ▸
                          // chevron still expands). Closed: the L1 row expands, as before.
                          if (showChain) { setChainL1(l1Id); return; }
                          if (showL1 && hasDetailRows) toggleL1(l1Id);
                        }}
                        onContextMenu={(e) => openDiscuss(e, row)}
                        style={{cursor: showChain || (showL1 && hasDetailRows) ? 'pointer' : 'default'}}
                      >
                        <td className="status-cell">
                          {showL1 && (
                            <button
                              type="button"
                              className={`status-dot status-dot--${statusOf[l1Id]}`}
                              title={`${GROUP_STATUS_LABEL[statusOf[l1Id]]} — show the chain`}
                              aria-label={`${GROUP_STATUS_LABEL[statusOf[l1Id]]}. Show the chain of ${itemKeyOf(l1Item) || 'this item'}`}
                              onClick={(e) => { e.stopPropagation(); openChain(l1Id); }}
                            >
                              {statusOf[l1Id] === 'ok' && (
                                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3.5 8.5l3 3 6-6.5" /></svg>
                              )}
                              {statusOf[l1Id] === 'gap' && (
                                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M8 4v5M8 12h.01" /></svg>
                              )}
                              {statusOf[l1Id] === 'notexec' && (
                                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="8" cy="8" r="4.5" /><path d="M8 5.8V8l1.5 1.2" /></svg>
                              )}
                            </button>
                          )}
                        </td>
                        {configuredLevels.map((level, levelIdx) => {
                          const columns = getColumnsForLevel(level.level_num);
                          const levelKey = `l${level.level_num}`;

                          // The level where the chain breaks shows a single pill
                          // ("Missing" for a genuine gap, "Not Executed" for a test with
                          // no run yet) in the first column; levels beyond stay blank so
                          // the grid lines stay continuous.
                          if (breakIndex !== -1 && levelIdx >= breakIndex) {
                            const isBreakCol = levelIdx === breakIndex;
                            return (
                              <React.Fragment key={`${groupIdx}-${rowInGroupIdx}-${levelIdx}`}>
                                {isMixedLevel(level) && (
                                  <td data-level={level.level_num} className={`mx-type-cell${levelIdx > 0 ? ' lvl-start' : ''}`} />
                                )}
                                {columns.map((col, colIdx) => (
                                  <td
                                    key={`${groupIdx}-${rowInGroupIdx}-${levelIdx}-${colIdx}`}
                                    data-level={level.level_num}
                                    className={`${colIdx === 0 && levelIdx > 0 && !isMixedLevel(level) ? 'lvl-start ' : ''}fit-${fitKind(col)}`}
                                    style={levelIdx > 0 ? { paddingLeft: `${fitScreen ? 8 + levelIdx * 2 : 18 + (levelIdx * 4)}px` } : undefined}
                                  >
                                    {isBreakCol && colIdx === 0 && (
                                      breakKind === 'missing' ? (() => {
                                        // A gap is a dashed slot with its own ＋ — add the missing item
                                        // straight from where it is missing.
                                        const parentItem = breakIndex > 0
                                          ? row[`l${configuredLevels[breakIndex - 1].level_num}`]
                                          : null;
                                        const options = addChildOptions(parentItem, breakIndex - 1);
                                        const title = options.length ? addTitle(parentItem, options) : '';
                                        return (
                                          <span className="gap-slot">
                                            <MissingIndicator />
                                            {options.length > 0 && (
                                              <button
                                                type="button"
                                                className="gap-add-btn"
                                                title={title}
                                                aria-label={title}
                                                aria-haspopup={options.length === 1 ? undefined : 'menu'}
                                                onClick={(e) => startAdd(e, parentItem, breakIndex - 1, l1Id, options)}
                                              >
                                                <PlusIcon />
                                              </button>
                                            )}
                                          </span>
                                        );
                                      })() : <NotExecutedIndicator />
                                    )}
                                  </td>
                                ))}
                              </React.Fragment>
                            );
                          }

                          // Printed once per new chain (see levelVisible above)
                          const item = levelVisible[levelKey] ? row[levelKey] : null;

                          return (
                            <React.Fragment key={`${groupIdx}-${rowInGroupIdx}-${levelIdx}`}>
                              {isMixedLevel(level) && (
                                <td data-level={level.level_num} className={`mx-type-cell${levelIdx > 0 ? ' lvl-start' : ''}`}>
                                  {depthTypeBadge(level, item)}
                                </td>
                              )}
                              {columns.map((col, colIdx) => {
                                const cellContent = item && !isColumnHidden(item, level.level_num, col)
                                  ? renderCell(item.columns[col], col)
                                  : renderCell(null, col);

                                const isL1FirstCell = showL1 && levelIdx === 0 && colIdx === 0;
                                const isItemLead = item && colIdx === 0;
                                // Every column except the leading (key) one is width-clamped.
                                // The lead cell is left alone: it's a short key, and its hover
                                // affordances spill outside the cell (they'd be clipped).
                                const isClamped = colIdx > 0;
                                return (
                                  <td
                                    key={`${groupIdx}-${rowInGroupIdx}-${levelIdx}-${colIdx}`}
                                    data-level={level.level_num}
                                    className={`${cellContent.className}${isL1FirstCell ? ' l1-cell' : ''}${colIdx === 0 && levelIdx > 0 && !isMixedLevel(level) ? ' lvl-start' : ''}${item && recentlyAddedIds.has(item.id) ? ' just-added' : ''}${isItemLead ? ' item-lead' : ''}${isClamped ? ' cell-clamp' : ''} fit-${fitKind(col)}`}
                                    style={levelIdx > 0 ? { paddingLeft: `${fitScreen ? 8 + levelIdx * 2 : 18 + (levelIdx * 4)}px` } : undefined}
                                    title={isClamped && (!wrapText || fitScreen) ? cellTooltip(cellContent) : undefined}
                                  >
                                    {colIdx === 0 ? (
                                    <span className="cell-lead">
                                    {/* Feature 4: chevron on first L1 column when detail rows exist; spacer otherwise keeps keys aligned */}
                                    {isL1FirstCell && hasDetailRows && (
                                      <span
                                        className={`chevron ${!isExpanded ? 'collapsed' : ''}`}
                                        onClick={(e) => { e.stopPropagation(); toggleL1(l1Id); }}
                                      >
                                        <ChevronIcon />
                                      </span>
                                    )}
                                    {isL1FirstCell && !hasDetailRows && (
                                      <span className="chevron-spacer" />
                                    )}
                                    {renderCellBody(col, cellContent, item?.url)}
                                    {/* Hover affordances (info + add) are absolutely positioned just after the
                                        item's key (left:100% of this anchor) so they stay attached to the item
                                        yet never reserve cell width — otherwise the invisible ＋ Add pill padded
                                        the Key column into a large empty gap before Name. */}
                                    {item && (
                                    <span className="cell-hover-actions">
                                    {/* Info card trigger: small icon on the item's first cell; hover it to preview details */}
                                    {isItemLead && (
                                      <button
                                        type="button"
                                        className="cell-info-btn"
                                        aria-label="Show item details"
                                        onMouseEnter={(e) => showItemCard(e, item)}
                                        onMouseLeave={hideItemCard}
                                        onFocus={(e) => showItemCard(e, item)}
                                        onBlur={hideItemCard}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <InfoIcon />
                                      </button>
                                    )}
                                    {/* Inline add: on the parent item's first cell, a single ＋ creates a
                                        child at the next level. When that depth mixes several block types,
                                        the ＋ opens a small menu of "Add <type>" options instead of many buttons. */}
                                    {(() => {
                                      // Inline add: same options as the gap slot and the chain panel.
                                      const options = addChildOptions(item, levelIdx);
                                      if (!options.length) return null;
                                      const title = addTitle(item, options);
                                      return (
                                        <button
                                          type="button"
                                          className="cell-add-btn"
                                          title={title}
                                          aria-label={title}
                                          aria-haspopup={options.length === 1 ? undefined : 'menu'}
                                          onClick={(e) => startAdd(e, item, levelIdx, l1Id, options)}
                                        >
                                          <PlusIcon />
                                        </button>
                                      );
                                    })()}
                                    </span>
                                    )}
                                    </span>
                                    ) : (
                                      // A block wrapper is what actually caps the column: under
                                      // `table-layout: auto` a max-width on the <td> alone is not
                                      // reliably honoured, but the wrapper's max-width caps the
                                      // cell's max-content contribution, so the column can't grow.
                                      <span className="cell-body">
                                        {renderCellBody(col, cellContent, item?.url)}
                                      </span>
                                    )}
                                  </td>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    );
                  })}
                  {groupIdx < l1Groups.length - 1 && (
                    <tr className="divider-row">
                      <td colSpan={1 + configuredLevels.reduce((s, l) => s + gridColCount(l), 0)}></td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {!loading && l1Groups.length === 0 && (
              <tr className="matrix-empty-row">
                <td colSpan={1 + configuredLevels.reduce((s, l) => s + gridColCount(l), 0)}>
                  <div className="matrix-empty">
                    {pageGroups.length > 0 ? (
                      <>
                        <p>No {levelTypeLabel(configuredLevels[0]) || 'L1'} items on this page are “{STATUS_FILTERS.find((f) => f.id === statusFilter)?.label}”.</p>
                        <p><button type="button" className="matrix-empty-link" onClick={() => setStatusFilter('all')}>Show all</button></p>
                      </>
                    ) : (
                    <>
                    <p>No items yet.</p>
                    {canAdd
                      ? <p>Click <strong>＋ Add</strong> above to create the first requirement.</p>
                      : <p>There are no items in this panel’s base filter.</p>}
                    </>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {showChain && (
        <div className="chain-dock" style={{ width: chainWidth }}>
          <div
            className="chain-resize"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize the chain panel (arrow keys)"
            aria-valuenow={chainWidth}
            tabIndex={0}
            title="Drag to resize · double-click to reset"
            onPointerDown={startChainResize}
            onMouseDown={(e) => e.preventDefault()}
            onKeyDown={onChainResizeKey}
            onDoubleClick={() => setChainWidth(380)}
          />
          {renderChainPanel()}
        </div>
      )}
      </div>
      )}

      {viewMode === 'funnel' && (
        <div className="funnel-scroll">
          {funnelLoading && !funnel ? (
            <div className="funnel-empty">Loading…</div>
          ) : (
            <FunnelView funnel={funnel && funnel.funnel} onViewInMatrix={() => setViewMode('matrix')} />
          )}
        </div>
      )}

      {viewMode === 'dashboard' && (
        <div className="funnel-scroll">
          <TraceDashboard
            setupId={setupId}
            canManagePanels={canManagePanels}
            aiStudio={canAskPaul && canAdd}
            onOpenInMatrix={(key) => { setSearchInput(key); setSearch(key); setViewMode('matrix'); }}
          />
        </div>
      )}

      {viewMode === 'graph' && (
        <div className="graph-outer">
          {graphError && <div className="error-message">{graphError}</div>}
          {search && (
            <div className="l1-search-banner">
              <span>
                Showing chains for L1 items matching <strong>“{search}”</strong>
                {!graphLoading && ` · ${graphRows.length} chain${graphRows.length === 1 ? '' : 's'} on this page`}
              </span>
              <button type="button" onClick={clearSearch}>Clear search</button>
            </div>
          )}
          {graphLoading && graphRows.length === 0 ? (
            <div className="graph-empty">Loading graph…</div>
          ) : (!graphLoading && search && graphRows.length === 0) ? (
            <div className="graph-empty">No level-1 items match “{search}”.</div>
          ) : (
            <GraphView
              rows={graphRows}
              levels={graphLevels}
              canAdd={canAdd}
              searchTerm={search}
              onCreateRelation={createRelation}
              onDeleteRelation={deleteRelation}
              canDeleteTraceAt={canDeleteTraceAt}
              onReconcile={() => fetchGraph(true)}
              onFetchOrphans={fetchOrphans}
              onFetchParents={fetchParents}
              onFetchRelations={fetchRelations}
              onAddChild={openAddFromGraph}
              canAddChildType={(childLevel) => canAddBlockType(childBlockOf(childLevel, null))}
              typeLabels={typeLabels}
            />
          )}
        </div>
      )}

      {(viewMode === 'matrix' || viewMode === 'graph') && (
      <div className="matrix-footer">
        {viewMode === 'matrix' && (
          <div className="footer-pagination">
            <select
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(parseInt(e.target.value))}
              className="footer-rows-select"
              title="Rows per page"
            >
              <option value={10}>10 rows</option>
              <option value={25}>25 rows</option>
              <option value={50}>50 rows</option>
            </select>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>← Prev</button>
            <span>Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next →</button>
            <button className="footer-refresh" onClick={() => fetchMatrix(true)} title={matrixCachedAt ? `Cached ${formatBuiltAt(matrixCachedAt)} — click to refresh from Orcanos` : 'Refresh matrix data from Orcanos'} aria-label="Refresh matrix data from Orcanos">⟳</button>
          </div>
        )}
        {viewMode === 'graph' && (
          <div className="footer-pagination">
            <select
              value={graphPageSize}
              onChange={(e) => {
                const size = parseInt(e.target.value);
                localStorage.setItem(GRAPH_PAGE_SIZE_KEY, String(size));
                setGraphPage(1);
                setGraphPageSize(size);
              }}
              disabled={graphLoading}
              className="footer-rows-select"
              title="Chains per page"
            >
              {GRAPH_PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}/page</option>)}
            </select>
            <button onClick={() => setGraphPage(p => Math.max(1, p - 1))} disabled={graphPage === 1 || graphLoading}>← Prev</button>
            <span>Page {graphPage} of {graphTotalPages}</span>
            <button onClick={() => setGraphPage(p => Math.min(graphTotalPages, p + 1))} disabled={graphPage === graphTotalPages || graphLoading}>Next →</button>
            <button className="footer-refresh" onClick={() => fetchGraph(true)} disabled={graphLoading} title={graphCachedAt ? `Cached ${formatBuiltAt(graphCachedAt)} — click to refresh from Orcanos` : 'Refresh graph data from Orcanos'} aria-label="Refresh graph data from Orcanos"><span className={graphLoading ? 'refresh-spin' : undefined}>⟳</span></button>
          </div>
        )}
        {viewMode === 'matrix' && (
          <p className="footer-count">
            Showing {l1Groups.length} L1 items, {rows.filter(r => r.l2).length} L2 matches
            {reconciling && <span className="reconciling-note">· updating…</span>}
            {matrixCachedAt && <span style={{ marginLeft: 10, color: '#A8A1BE' }}>· cached {formatBuiltAt(matrixCachedAt)}</span>}
          </p>
        )}
      </div>
      )}

      {showExportModal && (
        <ExportModal
          setupId={setupId}
          panelName={configuredLevels[0]?.name || `Panel ${setupId}`}
          showTracedItems={showTracedItems}
          keepFilterOrder={keepFilterOrder}
          onClose={(isProcessing) => {
            setShowExportModal(false);
            // Reset progress so MatrixPage background polling can take over
            if (isProcessing) setBackgroundExportProgress(null);
          }}
          onProgressChange={setBackgroundExportProgress}
          onJobIdChange={(id) => {
            // Remember that this page started it — only such a job may auto-download here.
            if (id) sessionExportJobsRef.current.add(id);
            setCurrentExportJobId(id);
          }}
        />
      )}

      {addCtx && (
        <AddItemModal
          setupId={setupId}
          ctx={addCtx}
          canAskPaul={canAskPaul}
          onClose={() => setAddCtx(null)}
          onDone={handleAddDone}
          onAdded={handleAiAdded}
        />
      )}

      {toast && (
        <div
          className={`matrix-toast ${toast.kind}`}
          role="status"
          onClick={() => setToast(null)}
          title="Click to dismiss"
        >
          <span className="matrix-toast-ic">{toast.kind === 'warn' ? '⚠' : '✓'}</span>
          <span className="matrix-toast-msg">{toast.msg}</span>
        </div>
      )}

      <ItemInfoCard card={itemCard} onEnter={keepItemCard} onLeave={hideItemCard} />
      <AddMenu menu={addMenu} onClose={() => setAddMenu(null)} />

      {discussAt && (
        <DiscussMenu
          at={discussAt}
          context={discussAt.context}
          onClose={() => setDiscussAt(null)}
        />
      )}

      {levelCard && (
        <LevelInfoCard
          card={{
            x: levelCard.x,
            y: levelCard.y,
            name: levelCard.level.name || `L${levelCard.level.level_num}`,
            blocks: blocksOfLevel(levelCard.level).map((b) => ({
              project: resolveProjectLabel(b),
              type: levelTypeLabel(b),
              filter: b.filter_id != null && b.filter_id !== '' ? String(b.filter_id) : '',
              filterName: filterNameOf(b),
              filterUrl: filterListingUrl(orcanosUrl, b.version_id, b.work_item_type, b.filter_id),
              orphanFilter: b.orphan_filter_id != null && b.orphan_filter_id !== '' ? String(b.orphan_filter_id) : '',
            })),
          }}
          onEnter={keepLevelCard}
          onLeave={hideLevelCard}
        />
      )}
    </div>
  );
}

export default MatrixPage;
